import { WithBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import {
    Condition,
    createFilterRow,
    IComplexFilter,
    isComplexFilter
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { WithConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { EditSimpleIcon, GrowthNegativeIcon, GrowthPositiveIcon, IncomingIcon, OutgoingIcon } from "@components/icon";
import { formatDateToDateString, isSameDay } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { getInfoByPath, IFieldDef, IFieldDefFn, IGetValueArgs, not, TValidatorFn } from "@components/smart/FieldInfo";
import { AccountDefFormatter, getBasePairedDocumentFormFieldDef, getLabelsDef } from "@components/smart/GeneralFieldDefinition";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { TFilterDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { fetchAndSetItemsByInfo } from "@components/smart/smartSelect/SmartSelectAPI";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { getRow } from "@components/smart/smartTable/SmartTable.utils";
import { ICellValueObject, IRow, TId } from "@components/table";
import { ITokenProps } from "@components/token";
import Tokenizer from "@components/token/Tokenizer";
import { TEntityKey } from "@evala/odata-metadata";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { getBoundValue, getNestedValue, getNewItemsMaxId, setNestedValue } from "@odata/Data.utils";
import { getDocumentTypeCodeFromEntityType, getEntitySetByDocumentType } from "@odata/EntityTypes";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import {
    AccountAssignmentEntity,
    BankTransactionEntity,
    CashReceiptEntity,
    CbaCategoryEntity,
    DocumentAccountAssignmentSelectionEntity,
    DocumentCbaCategoryEntity,
    DocumentEntity,
    DocumentItemCbaCategoryEntity,
    DocumentItemEntity, DocumentItemGrantAssignmentEntity,
    EntitySetName,
    EntityTypeName,
    IAccountEntity,
    IBankTransactionEntity,
    ICashReceiptEntity,
    IDocumentCbaCategoryEntity,
    IDocumentItemCbaCategoryEntity,
    IItemLabelEntity,
    ILabelEntity,
    IPaymentDocumentItemAccountAssignmentEntity,
    IPaymentDocumentItemEntity,
    ISplitAccountAssignmentEntity,
    PaymentDocumentEntity,
    PaymentDocumentItemAccountAssignmentEntity,
    PaymentDocumentItemAccountAssignmentSelectionEntity,
    PaymentDocumentItemEntity,
    PaymentDocumentItemLabelSelectionEntity,
    SelectionEntity
} from "@odata/GeneratedEntityTypes";
import {
    BankTransactionTypeCode,
    DocumentTypeCode,
    PaymentDocumentItemTypeCode,
    PaymentStatusCode,
    SelectionCode
} from "@odata/GeneratedEnums";
import { createFilterValue, IFormatOptions, transformToODataString } from "@odata/OData.utils";
import { parseError } from "@odata/ODataParser";
import { ORIGINAL_CLEARED_AMOUNT_PATH } from "@pages/admin/vatRules/VatRules.global.utils";
import AccountSplitDialog from "@pages/banks/AccountSplitDialog";
import {
    businessPartnerDependentFields,
    loadPartner,
    setSavedContactItems
} from "@pages/businessPartner/BusinessPartner.utils";
import { IRegularDocumentItemExtendedEntity } from "@pages/documents/DocumentInterfaces";
import {
    handleGrantAssignmentChange,
    handleGrantItemAmountChange,
    shouldAddGrantToDefaultFields
} from "@pages/grant/Grant.utils";
import { TFieldsDefinition } from "@pages/PageUtils";
import {
    getCompanyCurrency,
    getCompanyCurrencyUnit,
    isAccountAssignmentCompany,
    isCashBasisAccountingCompany,
    isNonProfitCompany
} from "@utils/CompanyUtils";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { arrayInsert, forEachKey, isDefined, isNotDefined, isObjectEmpty, roundToDecimalPlaces } from "@utils/general";
import i18next, { TFunction } from "i18next";
import React, { ReactElement } from "react";
import { DefaultTheme } from "styled-components";
import { ValidationError } from "yup";

import {
    BANK_ACCOUNT_PROFORMA_ISSUED,
    BANK_ACCOUNT_PROFORMA_RECEIVED,
    CREATE_CLEARING_SPLIT_ITEMS,
    DASH_CHARACTER,
    DEFAULT_CASH_BOXES_BALANCE_SHEET_ACCOUNT_PREFIX,
    REST_API_URL,
    UPDATE_CLEARING_SPLIT_ITEMS
} from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { WithPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import {
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    IconSize,
    LabelStatus,
    NavigationSource,
    Status,
    TextAlign,
    ValidationErrorType,
    ValidatorType,
    ValueType
} from "../../../enums";
import { BoldText, ColoredText, SmallText } from "../../../global.style";
import { TRecordAny, TRecordType, TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import { TableStorage } from "../../../model/TableStorage";
import { ValidationMessage } from "../../../model/Validator.types";
import TestIds from "../../../testIds";
import { default as Currency, default as CurrencyType, formatCurrency } from "../../../types/Currency";
import DateType, { getUtcDate } from "../../../types/Date";
import NumberType, { currencyScaleFormatter, currencyScaleParser, isNumber } from "../../../types/Number";
import { getOrigValue } from "../../../views/formView/Form.utils";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { ITableViewBaseProps } from "../../../views/table/TableView";
import {
    accountsAreClosedValidator,
    getAccAssignmentFilterWithTypeAndDate,
    getAccountAssignmentItemDef,
    getChoAIdBasedOnDate,
    getCurrentCoAId,
    getExpenseGains,
    handleItemAccAssignmentChange,
    handleItemAccAssignmentDialog,
    handleItemDateChange,
    IAccAssDialogCustomData,
    ItemCreateCustomAssignmentBc,
    loadAccountsBasedOnDate,
    prepareAccountAssignmentsForSave
} from "../../accountAssignment/AccountAssignment.utils";
import { PAIR_ACTION_ID } from "../../asset/PairingWithAssetTableView";
import { CBA_CATEGORY_PATH, handleItemCbaCategoryChange } from "../../cashBasisAccounting/CashBasisAccounting.utils";
import {
    getFiscalDataCorrespondingToDateAccountingTransaction,
    getItemLabelTitleFromSelectionCode,
    isMatchingAccount,
    isReceived,
    refreshExchangeRateWithoutDefault
} from "../../documents/Document.utils";
import { getActiveFiscalYears, getFiscalYears, isInFYorPeriod } from "../../fiscalYear/FiscalYear.utils";
import { replaceWildcardsAfterDateChange, setMatchingNumberRange } from "../../numberRange/NumberRange.utils";
import { ExchangeRateDiffLabel, GainWrapper, IconWrapper, LinkStyledBasic, LinkText } from "./BankTransactions.styles";

export type TPairedDocumentCategoriesMap = Record<number, (IDocumentCbaCategoryEntity | IDocumentItemCbaCategoryEntity)[]>;

export const ExchangeGainTitleLocalPath = BindingContext.localContext("ExchangeGainTitle");
export const GainAmountLocalPath = BindingContext.localContext("GainAmount");

// todo not sure about this, maybe it should be moved (and renamed) somewhere to pairing, not BankTransactions
// it is used heavily across all bank related files, but i'm not sure if i managed to apply it properly
export interface IBankCustomData extends IFormStorageDefaultCustomData {
    oldAssSelId?: SelectionCode;
    pairedDialogOpened?: boolean;
    type?: DocumentTypeCode;

    promptDialogOpened?: boolean;
    rootStorage?: FormStorage<unknown, IBankCustomData>;
    pairedStorage?: TSmartODataTableStorage<IBankCustomData>;
    pairedDocuments?: Record<TEntityKey, IPaymentDocumentItemEntityExpanded>;
    pairRedirectUrl?: string;
    showSaveButton?: boolean;
    statementPairedDialogOpened?: boolean;
    accountIdForCreationDialog?: boolean;
    documentsExchangeRates?: Map<number, number>;

    amountDialogRow?: IRow;
    defaultExchangeRate?: number;
    pairedDocumentCategoriesMap?: TPairedDocumentCategoriesMap;
    dialogCurrentLineItem?: TRecordAny;
    isCbaCategoryDialogOpen?: boolean;
}

export interface IBankStatementsCustomData extends IFormStorageDefaultCustomData {
    hideTransactions?: boolean;
}

export const getCorrent6Digit = (n: number): string => {
    return String(n).padStart(6, "0");
};

export const setNumberOurs = (numberOurs: string, count: number, storage: FormStorage): void => {
    if (!numberOurs) {
        storage.data.entity.NumberOurs = "";
    } else {
        const transCount = (count || 0) + 1;
        storage.data.entity.NumberOurs = `${numberOurs}-${getCorrent6Digit(transCount)}`;
    }
};

export const createDefaultPairFilter = (storage: TSmartODataTableStorage, transactionType: BankTransactionTypeCode): void => {
    const path = transactionType === BankTransactionTypeCode.OutgoingPayment ? "TransactionAmountToPay" : "TransactionAmountToReceive";

    storage.setFilterValueByPath(path, [createFilterRow({
        value: 0,
        condition: Condition.GreaterThan
    })]);
};

export const getTranBalance = (storage: FormStorage): number => {
    const data = storage.data.entity;
    const tranAmount = Math.abs(data.TransactionAmount) || 0;

    const sum = (storage.data.entity.Items || []).reduce((accumulator: number, link: IPaymentDocumentItemEntity) => {
        const add = isPaymentOrOther(link.PaymentDocumentItemTypeCode) ? link.TransactionAmount || 0 : 0;
        return accumulator + add;
    }, 0);

    return roundToDecimalPlaces(2, tranAmount - sum);
};

export const bankTransactionTypeFormatter = (val: TValue): ICellValueObject => {
    if (val === BankTransactionTypeCode.OutgoingPayment) {
        const title = i18next.t("Banks:Transactions.Out");

        return {
            tooltip: title,
            value: <OutgoingIcon title={title} width={IconSize.M}/>
        };
    }

    const title = i18next.t("Banks:Transactions.In");

    return {
        tooltip: title,
        value: <IncomingIcon title={title} width={IconSize.M}/>
    };
};

export const formatNumberCurrency = (val: number): number => {
    if (isNaN(val)) {
        return val;
    }
    return roundToDecimalPlaces(2, val);
};


export interface ICalculateExchangeRate {
    docCurrency: string;
    tranCurrency: string;
    tranExRate: number;
    docExchangeRate: number;
    docType: DocumentTypeCode;
    currentAmount: number;
    currentExRate?: number;
    ratedAmount?: number;
    type: BankTransactionTypeCode;
    context: IAppContext;
}

export const hasExchangeRate = (docCurrency: string, tranCurrency: string, context: IAppContext): boolean => {
    const companyCurrency = getCompanyCurrency(context);
    return docCurrency !== companyCurrency || tranCurrency !== companyCurrency;
};

export const hasExchangeGain = (docCurrency: string, docType: DocumentTypeCode, context: IAppContext): boolean => {
    const isProforma = docType === DocumentTypeCode.ProformaInvoiceReceived || docType === DocumentTypeCode.ProformaInvoiceIssued;
    return docCurrency !== getCompanyCurrency(context) && !isProforma;
};

export const hasAdditionalExRate = (docCurrency: string, tranCurrency: string, context: IAppContext): boolean => {
    // Every pair with different currencies
    return docCurrency !== tranCurrency && docCurrency !== getCompanyCurrency(context);
};

const getAmountFilterCustomization = (prefix?: string): Partial<TFilterDef> => ({
    filter: {
        oDataFn: "abs"
    }
});

export const getAmountFilterDefs = (): Record<string, TFilterDef> => ({
    TransactionAmount: {
        label: i18next.t("Banks:Transactions.Converted"),
        ...getAmountFilterCustomization("Transaction")
    },
    Amount: {
        label: i18next.t("Banks:Transactions.Amount"),
        ...getAmountFilterCustomization()
    },
});

export const getTransactionTypeFromEntity = (storage: FormStorage): BankTransactionTypeCode => {
    if (storage.data.entity.BankTransactionType?.Code) {
        return storage.data.entity.BankTransactionType?.Code;
    }

    const type = storage.getCustomData<IBankCustomData>().type;

    return type === DocumentTypeCode.CashReceiptReceived ? BankTransactionTypeCode.IncomingPayment : BankTransactionTypeCode.OutgoingPayment;
};

export const calculateExchangeRate = (args: ICalculateExchangeRate) => {
    // current ex rate is by default loaded from document ex rate (with fiscal year stuff) so it should be sufficient to compare these two.
    const _hasAdditionalExRate = hasAdditionalExRate(args.docCurrency, args.tranCurrency, args.context);
    const isDiffExRate = _hasAdditionalExRate ? args.currentExRate !== args.docExchangeRate : args.docExchangeRate !== args.tranExRate;
    const _hasExpenseGain = hasExchangeGain(args.docCurrency, args.docType, args.context) && isDiffExRate;

    let exchangeRateDiff = 0;

    if (_hasExpenseGain) {
        if (_hasAdditionalExRate) {
            // we have to round every calculation as it may lead to missing values in some small rounding
            if (isNotDefined(args.ratedAmount)) {
                exchangeRateDiff = NaN;
            } else {
                exchangeRateDiff = formatNumberCurrency(args.currentAmount * (args.tranExRate ?? 1)) -
                    formatNumberCurrency(args.ratedAmount * args.docExchangeRate);
            }
        } else {
            const transactionExchangeRate = args.tranExRate;
            exchangeRateDiff = formatNumberCurrency(args.currentAmount * transactionExchangeRate) -
                formatNumberCurrency(args.currentAmount * args.docExchangeRate);
        }

        const isOutgoing = args.type === BankTransactionTypeCode.OutgoingPayment;
        if (isOutgoing) {
            exchangeRateDiff *= -1;
        }
    }

    return _hasExpenseGain && !isNaN(exchangeRateDiff) ? formatNumberCurrency(exchangeRateDiff) : undefined;
};

export const handleBeforeSave = (storage: FormStorage, entity: IBankTransactionEntity | ICashReceiptEntity): IBankTransactionEntity | ICashReceiptEntity => {
    const tranRate = (entity as IBankTransactionEntity).BankTransactionType?.Code === BankTransactionTypeCode.OutgoingPayment ? -1 : 1;
    entity.TransactionAmount = Math.abs(entity.TransactionAmount || 0) * tranRate;

    const decimalPlaces = storage.data.entity.TransactionCurrency?.MinorUnit ?? 2;
    const rate = storage.data.entity?.ExchangeRatePerUnit ?? 1;

    entity.Amount = roundToDecimalPlaces(decimalPlaces, entity.TransactionAmount * rate);

    const isNew = storage.data.bindingContext.isNew();
    if (isNew) {
        entity.CurrencyCode = getCompanyCurrency(storage.context);
        entity.Company = { Id: storage.context.getCompany().Id };
    }

    if (storage.data.entity?.TransactionCurrency?.Code === getCompanyCurrency(storage.context)) {
        entity.ExchangeRatePerUnit = 1;
    }

    if (isAccountAssignmentCompany(storage.context)) {
        if (entity.Items) {
            entity.Items.forEach((item: IPaymentDocumentItemEntity) => {
                if (isPaymentOrOther(item.PaymentDocumentItemTypeCode)) {
                    prepareAccountAssignmentsForSave(storage, item, {
                        collectionName: "Items",
                        transactionDate: item.DateAccountingTransaction
                    });
                } else {
                    item.TransactionAmount = 0;
                    const coaId = getCurrentCoAId({
                        storage: storage
                    }, { transactionDate: item.DateAccountingTransaction });

                    setNestedValue(coaId, "AccountAssignmentSelection/AccountAssignment/ChartOfAccounts/Id", item);
                    setNestedValue(SelectionCode.Own, "AccountAssignmentSelection/Selection/Code", item);

                    const daNumber = getNestedValue("AccountAssignmentSelection/AccountAssignment/DebitAccount/Number", item);
                    const caNumber = getNestedValue("AccountAssignmentSelection/AccountAssignment/CreditAccount/Number", item);
                    const shortName = `${daNumber}/${caNumber}`;
                    const name = item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain ? i18next.t("Banks:Pairing.ExchangeGain") : i18next.t("Banks:Pairing.ExchangeLoss");
                    setNestedValue(name, "AccountAssignmentSelection/AccountAssignment/Name", item);
                    setNestedValue(shortName, "AccountAssignmentSelection/AccountAssignment/ShortName", item);
                }
            });

            // filter all gains for payments that have no account assignment
            entity.Items = entity.Items.filter((item: IPaymentDocumentItemEntity) => {
                if (isPaymentOrOther(item.PaymentDocumentItemTypeCode)) {
                    return true;
                }

                return itemHasAssignment(item, entity.Items);
            });
        }

        // @see https://solitea-cz.atlassian.net/browse/DEV-17687
        for (const item of (entity.Items ?? [])) {
            if ((item.AccountAssignmentSelection?.AccountAssignment as IEntity)?.[ItemCreateCustomAssignmentBc]) {
                const properties: (keyof IPaymentDocumentItemAccountAssignmentEntity)[] = ["DebitAccount", "CreditAccount"];

                for (const account of properties) {
                    const currentAccount = item.AccountAssignmentSelection.AccountAssignment[account] as IAccountEntity;
                    const {
                        accountPrefix,
                        accountSuffix
                    } = getFixedAccountAssignmentAccountNumberForPaymentEntity(storage.data.bindingContext, entity);

                    if (!currentAccount.Number.startsWith(accountPrefix)) {
                        continue;
                    }

                    const bc = storage.data.bindingContext.navigate("Items").addKey(item).navigate(`AccountAssignmentSelection/AccountAssignment/${account}`);
                    // expect items to be loaded, otherwise ItemCreateCustomAssignment wouldn't be set to true
                    const accounts = storage.getInfo(bc)?.fieldSettings?.items;
                    // the correct, analytical account number
                    // different for BankTransactions and CashReceipts
                    const correctAccountNumber = `${accountPrefix}${accountSuffix}`;
                    const correctAccountItem = accounts?.find(accountItem => (accountItem.additionalData as IAccountEntity).Number === correctAccountNumber);

                    if (correctAccountItem) {
                        (item.AccountAssignmentSelection.AccountAssignment[account] as IAccountEntity) = correctAccountItem.additionalData;
                    }
                }
            }
        }

    }

    return entity;
};


export const refreshFormAfterDateChange = async (storage: FormStorage, bc: BindingContext, newValue: Date): Promise<void> => {
    const oldValue = storage.getValue(bc);
    const fiscalData = getFiscalDataCorrespondingToDateAccountingTransaction(storage, newValue);
    const oldFiscalData = getFiscalDataCorrespondingToDateAccountingTransaction(storage, oldValue);

    const hasChanged = oldFiscalData?.fiscalYear?.Id !== fiscalData?.fiscalYear?.Id;
    if (fiscalData && hasChanged) {
        const changed = await setMatchingNumberRange(storage);
        if (changed) {
            replaceWildcardsAfterDateChange(storage);
        }

        storage.refresh();
    }
};


const finishClosingPrompt = (storage: FormStorage<unknown, IBankCustomData>) => {
    const url = storage.getCustomData().pairRedirectUrl;
    if (url) {
        storage.history.push(url);
    } else {
        storage.refresh();
    }
};

const cancelChanges = (storage: FormStorage<unknown, IBankCustomData>) => {
    storage.setCustomData({
        promptDialogOpened: false,
        pairedDialogOpened: false
    });
    finishClosingPrompt(storage);
};


export const getBankTransactionType = (storage: FormStorage): BankTransactionTypeCode => {
    return storage.data.entity.BankTransactionType?.Code;
};

export const getCashTransactionType = (storage: FormStorage<unknown, IBankCustomData>): BankTransactionTypeCode => {
    const type = storage.getCustomData().type;
    return type === DocumentTypeCode.CashReceiptReceived ? BankTransactionTypeCode.IncomingPayment : BankTransactionTypeCode.OutgoingPayment;
};


function getDefaultStatementFilters(context: IAppContext): TRecordType<boolean[] | IComplexFilter[]> {
    const filters: TRecordType<boolean[] | IComplexFilter[]> = {
        "BankAccount/IsActive": [true]
    };

    // const oldestOpenFiscalYear = getOpenFiscalYears(context)?.[0];
    // if (oldestOpenFiscalYear) {
    //     filters["DateFrom"] = [createFilterRow({
    //         value: oldestOpenFiscalYear.DateStart,
    //         condition: Condition.IsAfterOrEqualsTo
    //     })];
    // }

    return filters;
}

/**
 * Creates default filter values in storage, filters should be defined in Def, so user can work with them.
 * @param storage
 */
export function createDefaultStatementPairFilter(storage: TSmartODataTableStorage): void {
    const filters = getDefaultStatementFilters(storage.context);

    forEachKey(filters, (key) => {
        storage.setFilterValueByPath(key, filters[key]);
    });
}

export const createStatementFilterQuery = (context: IAppContext, baseBc: BindingContext): string => {
    const filters = getDefaultStatementFilters(context);
    const queries: string[] = [];

    forEachKey(filters, (path) => {
        const value = filters[path];

        value.forEach(val => {
            const query = isComplexFilter(val)
                ? createFilterValue(baseBc.navigate(path), path, val)
                : `${path} eq ${transformToODataString(val, ValueType.Boolean)}`;
            queries.push(query);
        });
    });

    return queries.join(" AND ");
};

export const refreshExRateAndRefreshItems = async (storage: FormStorage, currencyCode: string, date: Date): Promise<void> => {
    await refreshExchangeRateWithoutDefault(storage, currencyCode, date);
    storage.addActiveField(storage.data.bindingContext.navigate(BankTransactionEntity.Items));
    storage.refreshFields();
};

export const isPairingRowReadOnly = (pairItem: IPaymentDocumentItemEntity, storage: TableStorage, rootStorage: FormStorage): boolean => {
    // filled ID === item is already accounted
    if (pairItem?.Id) {
        const pairedDocBc = rootStorage.data.bindingContext.navigate("Items").addKey(pairItem?.Id);
        return !!rootStorage.getBackendDisabledFieldMetadata(pairedDocBc);
    }

    return false;
};

export const handleTranAmountChange = (storage: FormStorage, args: ISmartFieldChange): void => {
    if (args.bindingContext.getPath() === BankTransactionEntity.TransactionAmount) {
        const amountBc = args.bindingContext.getParent().navigate(BankTransactionEntity.Amount);
        const tranExRate = storage.getEntity().ExchangeRatePerUnit ?? 1;
        const valN = Number(args.value);
        if (!isNaN(valN)) {
            const val = roundToDecimalPlaces(2, valN * tranExRate);
            storage.setValue(amountBc, val);
        }
    }
};

export const handleDateChange = async (storage: FormStorage, args: ISmartFieldChange): Promise<void> => {
    if (args.bindingContext.getPath() === PaymentDocumentItemEntity.DateAccountingTransaction && args.triggerAdditionalTasks) {
        await handleItemDateChange({
            storage,
            bindingContext: args.bindingContext,
            value: args.value as Date
        });

        storage.refreshFields();
    }
};


export const handleLineItemsChange = async (storage: FormStorage, args: ISmartFieldChange): Promise<void> => {
    //BEFORE CHANGE
    if (handleGrantAssignmentChange(args, storage)) {
        return;
    }

    if (handleItemAccAssignmentDialog({ storage, e: args })) {
        // Account assignment dialog is shown -> do not process the change further as the dialog might be canceled
        // -> we don't want to keep "Selection.Own" in the select then.
        return;
    }
    handleGrantItemAmountChange(args, storage, true);
    handleTranAmountChange(storage, args);

    storage.handleLineItemsChange(args);

    // AFTER CHANGE
    handleDateChange(storage, args);
    handleItemCbaCategoryChange(args, storage);
    if (args.triggerAdditionalTasks) {
        await handleBankAssignmentChange(args, storage);
    }

    if (args.bindingContext.getPath() === PaymentDocumentItemLabelSelectionEntity.Labels) {
        const labelSelectionBc = args.bindingContext.getParent();
        const value = args.value as (string | number)[];
        if (!args.additionalData?.isNoRecord && value?.length > 0) {
            storage.setValue(labelSelectionBc.navigate(PaymentDocumentItemLabelSelectionEntity.SelectionCode), SelectionCode.Own);
            storage.setValue(labelSelectionBc.navigate(PaymentDocumentItemLabelSelectionEntity.Selection), { Code: SelectionCode.Own });
        } else {
            storage.setValue(labelSelectionBc, {
                Selection: { Code: SelectionCode.None },
                SelectionCode: SelectionCode.None,
                Labels: []
            });
        }
    }
};

export const paymentDocDateValidator: TValidatorFn = (value: TValue, { storage, bindingContext }) => {
    if (!DateType.isValid(value as Date)) {
        return new ValidationError(ValidationMessage.NotADate, false, bindingContext.getNavigationPath(true));
    }
    const origValue = getOrigValue(storage as FormStorage, bindingContext);

    if (!isSameDay(value as Date, origValue as Date) && isCashBasisAccountingCompany(storage.context)) {
        const date = value as Date;
        if (!getActiveFiscalYears(storage.context).some(fy => isInFYorPeriod(fy, date))) {
            const isInactive = getFiscalYears(storage.context).some(fy => isInFYorPeriod(fy, date));
            const translationPath = isInactive ? "ClosedCalendarYear" : "WrongCalendarYear";
            return new ValidationError(i18next.t(`Document:Form.${translationPath}`), false, bindingContext.getNavigationPath(false));
        }
    }
    return true;
};


interface ICanRowBePairedArgs {
    storage: TSmartODataTableStorage;
    origPairedDocuments: Record<TEntityKey, IPaymentDocumentItemEntityExpanded>;
    rowId: TId;
    paymentType: BankTransactionTypeCode;
    row?: IRow;
}

export const canRowBePaired = (args: ICanRowBePairedArgs) => {
    const row = args.row ?? getRow(args.storage.tableAPI.getState().rows, args.rowId);

    if (row?.customData) {
        const key = (args.rowId as BindingContext)?.getKey?.();
        const isPairedSaved = !!args.origPairedDocuments?.[key];
        const path = args.paymentType === BankTransactionTypeCode.OutgoingPayment ? "TransactionAmountToPay" : "TransactionAmountToReceive";

        const amount = row.customData.entity[path];

        return (isPairedSaved || amount > 0);
    }

    return true;
};

export const isPairingActive = (tableStorage: TableStorage) => {
    return (tableStorage as TableStorage).tableAction === PAIR_ACTION_ID;
};

export const insertBankStatementMetadataToEntity = (storage: FormStorage, entity: IEntity, esName: EntitySetName) => {

    // in drafts BE has (for its own reasons) Statement and not corresponding
    // Bank(Loan)Statement entity. So we have to change it to correct one.
    if (!isObjectEmpty(entity.BankStatement)) {
        const name = storage.oData.metadata.getTypeForPath(esName).getFullName();

        entity.BankStatement = {
            ...entity.BankStatement,
            _metadata: {
                "": { type: `#${name}` }
            }
        };
    }
};


export const PAYMENT_GROUP = "payment_group";
export const EXCHANGE_GAIN_GROUP = "exchange_group";

export const PairCoreAdditionalData = [
    {
        id: "Items/Amount"
    }, {
        id: "Items/TransactionAmount"
    }, {
        id: "Items/ClearedAmount"
    }, {
        id: "Items/AccountAssignmentSelection/AccountAssignment"
    }, {
        id: "Items/PaymentDocumentItemTypeCode"
    }, {
        id: "Items/SplitAccountAssignments/TransactionAmount"
    }, {
        id: "Items/SplitAccountAssignments/Description"
    }, {
        id: "Items/SplitAccountAssignments/CreditAccount"
    }, {
        id: "Items/SplitAccountAssignments/DebitAccount"
    },
];

export const PairAdditionalData = [...PairCoreAdditionalData, {
    id: "Items/LinkedDocument/NumberOurs"
}, {
    id: "Items/LinkedDocument/DocumentTypeCode"
}, {
    id: "Items/LinkedDocument/TransactionAmount"
}, {
    id: "Items/LinkedDocument/TransactionAmountDue"
}, {
    id: "Items/LinkedDocument/DateAccountingTransaction"
}, {
    id: `Items/${ItemCreateCustomAssignmentBc}`
}, {
    id: `Items/${ExchangeGainTitleLocalPath}`
}, {
    id: `Items/${GainAmountLocalPath}`
}, {
    id: "Items/Order"
}, {
    id: "Items/ExchangeRate"
}, {
    id: "Items/LinkedDocument/ExchangeRatePerUnit"
}, {
    id: "Items/LinkedDocument/TransactionCurrencyCode"
}, {
    id: "Items/Order"
}, {
    id: `${BindingContext.localContext("ItemsHeader")}`
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.IsAssetAcquisition)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.TaxImpactCode)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.TaxPercentage)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.Category, CbaCategoryEntity.Name)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.DocumentItems, DocumentItemEntity.CbaCategory, DocumentItemCbaCategoryEntity.IsAssetAcquisition)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.DocumentItems, DocumentItemEntity.CbaCategory, DocumentItemCbaCategoryEntity.TaxImpactCode)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.DocumentItems, DocumentItemEntity.CbaCategory, DocumentItemCbaCategoryEntity.TaxPercentage)
}, {
    id: createPath(PaymentDocumentEntity.Items, PaymentDocumentItemEntity.LinkedDocument, DocumentEntity.DocumentItems, DocumentItemEntity.CbaCategory, DocumentItemCbaCategoryEntity.Category, CbaCategoryEntity.Name)
}];

export const getBankTransItemDefaultDateAccountingTransaction = (storage: FormStorage): Date => {
    const name = BankTransactionEntity.DateBankTransaction;
    const dateBankTransaction = storage.getValueByPath(name);
    return dateBankTransaction ?? getUtcDate();
};

export const getCashReceiptItemDefaultDateAccountingTransaction = (storage: FormStorage): Date => {
    const name = CashReceiptEntity.DateIssued;
    const dateBankTransaction = storage.getValueByPath(name);

    return dateBankTransaction ?? getUtcDate();
};

export const getBankItemDefaultDateAccountingTransaction = (storage: FormStorage): Date => {
    const type = getDocumentTypeCodeFromEntityType(storage.data.bindingContext.getRootParent().getEntityType().getName() as EntityTypeName);
    const isBank = type === DocumentTypeCode.BankTransaction || type === DocumentTypeCode.LoanTransaction;

    return isBank ? getBankTransItemDefaultDateAccountingTransaction(storage) : getCashReceiptItemDefaultDateAccountingTransaction(storage);
};

const getTransactionAmountUnit = (args: IGetValueArgs) => {
    const currency = args.storage.data.entity?.TransactionCurrency?.Code ?? args.storage.data.entity?.TransactionCurrencyCode;
    return CurrencyType.getCurrencyUnit(currency ? currency : getCompanyCurrency(args.storage.context));
};

export const isPairAmountReadOnly = (storage: FormStorage, bc: BindingContext): boolean => {
    const row = storage.getValue(bc.getParent());
    const hasDoc = row?.LinkedDocument?.Id;
    return !!hasDoc;
};

const isExchangeRateGroupRow = (args: IGetValueArgs) => {
    // we can use the fact that for one group this field is always readonly
    // and for the other it is always not read only
    // so if the groupID is provided use it, otherwise we need to grab it from row
    if (isDefined(args.fastEntryListId)) {
        return args.fastEntryListId === EXCHANGE_GAIN_GROUP;
    }

    const row = args.storage.getValue(args.bindingContext.getParent());
    return !isPaymentOrOther(row?.PaymentDocumentItemTypeCode);
};

export const getPairDef = (type: DocumentTypeCode, hasAccountAssignment: boolean, includeLabels = false): TFieldsDefinition => {
    const accDefs = getAccountAssignmentItemDef("Items", type, false, hasAccountAssignment);

    const _getBankAccAssignment = (propName: string) => {
        const isDebit = propName === "DebitAccount";
        const filterStart = isDebit ? "5" : "6";
        const disabledStat = isDebit ? PaymentDocumentItemTypeCode.ExchangeGain : PaymentDocumentItemTypeCode.ExchangeLoss;
        const filterStat = isDebit ? PaymentDocumentItemTypeCode.ExchangeLoss : PaymentDocumentItemTypeCode.ExchangeGain;
        const defaultDef = accDefs[`Items/AccountAssignmentSelection/AccountAssignment/${propName}`];
        return {
            ...defaultDef,
            isRequired: (args: IGetValueArgs) => {
                // for dialog you have to show required and value is stored in temporal data
                // or in save when it is stored in entity
                const row = args.storage.getValue(args.bindingContext.getParent().getParent().getParent());
                if (Array.isArray(row) || args.isColumnInfo) {
                    // called from SmartFastEntryList -> returns full Items not only one item due different binding context
                    // always return true
                    return true;
                }
                const code = row?.AccountAssignmentSelection?.Selection?.Code;
                const temporalData = args.storage.getTemporalData(args.bindingContext.getParent())?.value;

                return (code === SelectionCode.Own || temporalData === SelectionCode.Own) && row?.PaymentDocumentItemTypeCode !== disabledStat;
            },
            formatter: (val: TValue, args?: IFormatOptions) => {
                
                const row = args.storage.getValue(args.bindingContext.getParent().getParent().getParent());
                if (!isPaymentOrOther(row?.PaymentDocumentItemTypeCode)) {
                    if (args.readonly) {
                        return typeof val === "object" ? (val as IEntity)?.Number : val;
                    }

                    const entity = args.item || args.entity;
                    return entity?.Number;
                }

                return AccountDefFormatter(val, args);
            },
            isReadOnly: (args: IGetValueArgs) => {
                const row = args.storage.getValue(args.bindingContext.getParent().getParent().getParent());
                return row?.PaymentDocumentItemTypeCode === disabledStat;
            },
            width: (args: IGetValueArgs) => {
                const row = args.storage.getValue(args.bindingContext.getParent().getParent().getParent());
                // check whether there is TypeCode prop too, don't use '?' can be called from different scopes
                if (row?.PaymentDocumentItemTypeCode && isPaymentOrOther(row.PaymentDocumentItemTypeCode)) {
                    return BasicInputSizes.L;
                }

                return BasicInputSizes.S;
            },
            fieldSettings: {
                ...defaultDef.fieldSettings,
                entitySet: (args: IGetValueArgs) => {
                    const isSplit = args.bindingContext.getFullPath().includes(PaymentDocumentItemEntity.SplitAccountAssignments);
                    let bc = args.bindingContext.getParent().getParent();
                    // split uses same fields but are stored in different path
                    if (!isSplit) {
                        bc = bc.getParent();
                    }

                    const date = args.storage.getValue(bc)?.DateAccountingTransaction
                        ?? getBankItemDefaultDateAccountingTransaction(args.storage as FormStorage);
                    const choaId = getChoAIdBasedOnDate(args.storage as FormStorage, date, false);

                    return `ChartsOfAccounts(${choaId})/Accounts`;
                },
                localDependentFields: [{
                    from: { id: "Number" },
                    to: { id: "Number" },
                    navigateFrom: NavigationSource.Itself
                }, {
                    from: { id: "Name" },
                    to: { id: "Name" },
                    navigateFrom: NavigationSource.Itself
                }],
                itemsForRender: (items: ISelectItem[], args: IGetValueArgs) => {
                    const row = args.storage.getValue((args.info as TRecordAny).bindingContext.getParent().getParent().getParent());
                    if (row?.PaymentDocumentItemTypeCode === filterStat) {
                        return items?.filter((item: ISelectItem) => {
                            return item.additionalData?.Number?.startsWith(filterStart);
                        });
                    }

                    return items;
                }
            }
        };
    };

    let def: TFieldsDefinition = {
        "Items/TransactionAmount": {
            width: BasicInputSizes.S,
            label: i18next.t("Common:General.Amount"),
            fieldSettings: {
                unit: getTransactionAmountUnit
            },
            // formatter: readOnlyCurrencyItemFormatter,
            isReadOnly: (args: IGetValueArgs) => {
                return isPairAmountReadOnly(args.storage as FormStorage, args.bindingContext);
            },
            parser: (value: TValue, args: IFormatOptions) => {
                return currencyScaleParser(value as string, args.storage.data.entity.TransactionCurrency);
            },
            validator: {
                type: ValidatorType.Custom,
                settings: {
                    customValidator: (value, args) => {
                        const code = args.storage.getValue(args.bindingContext.getParent())?.PaymentDocumentItemTypeCode;
                        const isPayment = isPaymentOrOther(code);
                        return isPayment ? isNumber(value) && parseFloat(value as string) > 0 : true;
                    },
                    message: i18next.t("Comnon:Validation.MustBePositiveNumber")
                }
            },
            affectedFields: [{
                navigateFromParent: true,
                id: "Amount"
            }],
            customizationData: {
                dependents: ["Amount"],
            }
        },
        "Items/Amount": {
            width: BasicInputSizes.S,
            fieldSettings: {
                unit: (args) => CurrencyType.getCurrencyUnit(getCompanyCurrency(args.storage.context))
            },
            customizationData: {
                useForCustomization: false
            },
            isReadOnly: true,
            isRequired: false,
            label: i18next.t("Banks:Transactions.Accounted")
        }
    };

    if (type !== DocumentTypeCode.LoanTransaction) {
        def = {
            ...def,
            "Items/LinkedDocument": {
                ...getBasePairedDocumentFormFieldDef(),
                isVisible: (args: IGetValueArgs) => {
                    // visible if at least one row has value
                    // we cannot have column only for some rows, it would break virtualization
                    // (because rows can wrap and every row has to have same height)
                    const entity = args.storage.data.entity as IBankTransactionEntity;

                    return entity?.Items?.some(item => item.LinkedDocument?.NumberOurs);
                }
            }
        };
    }

    if (hasAccountAssignment) {
        def = {
            ...def,
            ...accDefs,
            "Items/SplitAccountAssignments/TransactionAmount": {
                useForValidation: true,
                fieldSettings: {
                    unit: getTransactionAmountUnit
                },
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (value: TValue, args: IGetValueArgs, testContext): boolean | ValidationError => {
                            let errorKey: string;

                            if (!isNumber(value)) {
                                errorKey = "NotaNumber";
                            }

                            if (NumberType.parse(value?.toString()) === 0) {
                                errorKey = "ZeroNotAllowed";
                            }

                            return errorKey
                                ? new ValidationError(i18next.t(`Common:Validation.${errorKey}`), value, args.bindingContext.getNavigationPath())
                                : true;
                        }
                    }
                },
                formatter: (value: TValue, args: IFormatOptions) => currencyScaleFormatter(value as number, args.entity.TransactionCurrency),
                parser: (value: TValue, args: IFormatOptions) => {
                    const maximumFractionDigits = args.entity.TransactionCurrency?.MinorUnit ?? 2;
                    return NumberType.parse(value as string, true, {
                        maximumFractionDigits
                    });
                },
                isRequired: true,
                customizationData: {
                    useForCustomization: false
                }
            },
            "Items/SplitAccountAssignments/Description": {
                useForValidation: true,
                isRequired: true,
                customizationData: {
                    useForCustomization: false
                }
            },
            "Items/SplitAccountAssignments/CreditAccount": {
                ..._getBankAccAssignment("CreditAccount"),
                isRequired: true
            },
            "Items/SplitAccountAssignments/DebitAccount": {
                ..._getBankAccAssignment("DebitAccount"),
                isRequired: true
            },

            "Items/DateAccountingTransaction": {
                isRequired: not(isExchangeRateGroupRow),
                label: i18next.t("Banks:Transactions.DateTransactionLabel"),
                isReadOnly: isExchangeRateGroupRow,
                defaultValue: (args: IGetValueArgs) => {
                    return getBankItemDefaultDateAccountingTransaction(args.storage as FormStorage);
                }
            },
            [`Items/${ExchangeGainTitleLocalPath}`]: {
                type: FieldType.Custom,
                width: FastEntryInputSizes.XS,
                labelStatus: LabelStatus.Hidden,
                label: null,
                // smart fast entry list will call render only if there is a formatter (I am FAR from being that brave to change that, hence I add it here :)
                formatter: (val: TValue) => {
                    return val as string;
                },
                render: (args: IFieldDefFn) => {
                    const row = args.storage.getValue(args.props.info.bindingContext.getParent());
                    const type = row?.PaymentDocumentItemTypeCode;
                    const isLoss = type === PaymentDocumentItemTypeCode.ExchangeLoss;
                    return getExpenseTitle(isLoss, { width: args.props.width, withMargin: true });
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            [`Items/${GainAmountLocalPath}`]: {
                isReadOnly: true,
                isRequired: false,
                width: FastEntryInputSizes.S,
                textAlign: TextAlign.Right,
                label: i18next.t("Banks:Pairing.Accounted"),
                formatter: (val: TValue, args?: IFormatOptions) => {
                    const row = args.storage.getValue(args.bindingContext.getParent());
                    const amount = row?.Amount;
                    return Currency.format(Math.abs(amount), { currency: getCompanyCurrency(args.storage.context), minimumFractionDigits: 2, withUnit: false });
                },
                fieldSettings: {
                    unit: getCompanyCurrencyUnit
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            "Items/AccountAssignmentSelection/AccountAssignment": {
                ...accDefs["Items/AccountAssignmentSelection/AccountAssignment"],
                defaultValue: "",
                label: i18next.t("Banks:Transactions.AccountAssignmentFormLabel"),
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: [{ validator: accountsAreClosedValidator }]
                    }
                },
                filter: {
                    select: (args: IGetValueArgs) => {
                        // TODO: rename in IGetValueArgs to IFieldInfo
                        const bc = (args.info as IFieldInfo).bindingContext;
                        const date = args.storage.getValue(bc.getParent().getParent())?.DateAccountingTransaction ?? getBankItemDefaultDateAccountingTransaction(args.storage as FormStorage);
                        let filter = getAccAssignmentFilterWithTypeAndDate(type, args, date);
                        const path = bc.getRootParent().getPath(true);

                        let accountPrefix: string;
                        if (path === EntitySetName.BankTransactions || path === EntitySetName.LoanTransactions) {
                            accountPrefix = args.data.BankStatement?.BankAccountBase?.BalanceSheetAccountNumberPrefix;
                        } else {
                            accountPrefix = getCashboxPrefix(args.data);
                        }

                        if (accountPrefix) {
                            const relevantAccountSide = getRelevantAccountSide(args.storage as FormStorage);
                            filter += ` AND startswith(${relevantAccountSide}/Number,'${accountPrefix}')`;
                        }

                        return filter;
                    }
                },
                fieldSettings: {
                    ...accDefs["Items/AccountAssignmentSelection/AccountAssignment"].fieldSettings,

                    onBeforeRequest: async (args) => {
                        // transformFetchedItems is dependent on accounts
                        // we need to fetch them here, so that they are already stored in info before transformFetchedItems is called
                        // this shouldn't add any unnecessary requests, because fetchItems is memoized
                        const relevantAccountSide = getRelevantAccountSide(args.storage as FormStorage);
                        const accountsBc = args.bindingContext.navigate(relevantAccountSide);

                        // if it is linked document add "Split" decision
                        const row = args.storage.getValue(args.bindingContext.getParent().getParent());
                        if (row?.LinkedDocument?.Id) {
                            const hasSplit = !!args.info.fieldSettings.additionalItems?.find(item => item.id === SelectionCode.Split);
                            if (!hasSplit) {
                                args.info.fieldSettings.additionalItems.splice(1, 0, {
                                    id: SelectionCode.Split,
                                    label: i18next.t("Document:AccountAssignment.Split"),
                                    groupId: "Default"
                                });
                            }
                        }

                        // use fetchAndSetItemsByInfo instead of fetchSelectItem
                        // so that we can retrieve it in transformFetchedItems without using some custom data
                        await fetchAndSetItemsByInfo(args.storage as FormStorage, args.storage.getInfo(accountsBc));
                    },
                    transformFetchedItems: (items: ISelectItem[], args: IGetValueArgs): ISelectItem[] => {
                        const relevantAccountSide = getRelevantAccountSide(args.storage as FormStorage);
                        const origBc = (args.info as IFieldInfo).bindingContext;
                        const accountsBc = origBc.navigate(relevantAccountSide);
                        const accounts = args.storage.getInfo(accountsBc)?.fieldSettings?.items ?? [] as ISelectItem[];
                        const entity = args.storage.data.entity;
                        const {
                            accountPrefix,
                            accountSuffix
                        } = getFixedAccountAssignmentAccountNumberForPaymentEntity(args.storage.data.bindingContext, entity);

                        // the correct, analytical account number
                        // different for BankTransactions and CashReceipts
                        const correctAccountNumber = `${accountPrefix}${accountSuffix}`;
                        const correctAccountItem = accounts.find(accountItem => (accountItem.additionalData as IAccountEntity)?.Number === correctAccountNumber);

                        if (!correctAccountItem) {
                            // analytical account doesn't exist
                            return items;
                        }

                        return items
                            // remove items that already use analytical account, but different one
                            .filter(item => {
                                const account = item.additionalData[relevantAccountSide] as IAccountEntity;

                                if (!account?.Number?.startsWith(accountPrefix)) {
                                    return true;
                                }

                                return account.Number === accountPrefix || account.Number === correctAccountNumber;
                            })
                            .map((item) => {
                                const account = item.additionalData[relevantAccountSide] as IAccountEntity;

                                if (account?.Number?.startsWith(accountPrefix) && account?.Number !== correctAccountNumber) {
                                    const correctLabel = item.label.replace(accountPrefix, correctAccountNumber);

                                    return {
                                        ...item,
                                        label: correctLabel,
                                        tabularData: [
                                            correctLabel,
                                            ...item.tabularData.slice(1)
                                        ],
                                        additionalData: {
                                            ...item.additionalData,
                                            [relevantAccountSide]: correctAccountItem.additionalData,
                                            ShortName: correctLabel
                                        }
                                    };
                                } else {
                                    return item;
                                }
                            });
                    }
                }
            },
            "Items/AccountAssignmentSelection/AccountAssignment/CreditAccount": _getBankAccAssignment("CreditAccount"),
            "Items/AccountAssignmentSelection/AccountAssignment/DebitAccount": _getBankAccAssignment("DebitAccount")
        };
    }

    if (includeLabels) {
        def = {
            ...def,
            "Items/LabelSelection/Labels": {
                ...getLabelsDef("Document"),
                width: FastEntryInputSizes.M,
                textAlign: TextAlign.Left,
                isReadOnly: (args: IGetValueArgs) => {
                    const row = args.storage.getValue(
                        args.bindingContext.getParent().getParent()
                    ) as IPaymentDocumentItemEntity;
                    return !!row?.LinkedDocument?.Id;
                },
                additionalProperties: [
                    { id: "/Items/LabelSelection/Labels/Label/Name" },
                    { id: "/Items/LabelSelection/Labels/LabelHierarchy/Color" },
                ],
                formatter: (_, args) => {
                    const labelSelection = args.storage.getValue(args.bindingContext.getParent());
                    const selectionCodeTitle = getItemLabelTitleFromSelectionCode(labelSelection);
                    const tokensDef: ITokenProps[] = labelSelection?.Labels?.map((label: IItemLabelEntity) => ({
                        id: label.Id,
                        title: label.Label.Name,
                        color: label.LabelHierarchy.Color
                    })) ?? [];
                    if (tokensDef.length === 0) {
                        return `${DASH_CHARACTER} ${selectionCodeTitle}`;
                    }
                    return {
                        value: <Tokenizer tokens={tokensDef}
                                          maxVisibleTokens={4}
                                          trailingText={selectionCodeTitle}
                                          showMoreTokensTooltip/>,
                        tooltip: ""
                    };
                },
                fieldSettings: {
                    ...getLabelsDef("Document").fieldSettings,
                    noRecordText: (args: IGetValueArgs) => {
                        const row = args.storage.getValue(
                            args.bindingContext.getParent().getParent()
                        ) as IPaymentDocumentItemEntity;
                        return row?.LinkedDocument?.Id ? null : i18next.t("Document:Labels.None");
                    },
                    renderDefaultGroupWithoutCheckboxes: true,
                    defaultChecked: SelectionCode.None,
                   
                },
                comparisonFunction: (entity1: IEntity, entity2: IEntity, bc: BindingContext): boolean => {
                    const selectionCodeBc = bc
                        .getParent()
                        .navigate(PaymentDocumentItemLabelSelectionEntity.SelectionCode);
                    const dataBindingContext = bc.getRootParent();
                    const selection1 = getBoundValue({
                        bindingContext: selectionCodeBc,
                        data: entity1,
                        dataBindingContext,
                    });
                    const selection2 = getBoundValue({
                        bindingContext: selectionCodeBc,
                        data: entity2,
                        dataBindingContext,
                    });
                    if (selection1 !== selection2) {
                        return false;
                    }
                    const labels1 = getBoundValue({ bindingContext: bc, data: entity1, dataBindingContext });
                    const labels2 = getBoundValue({ bindingContext: bc, data: entity2, dataBindingContext });
                    return (
                        labels1?.length === labels2?.length &&
                        (!labels1?.length ||
                            labels1?.every((l1: ILabelEntity) => !!labels2.find((l2: ILabelEntity) => l2.Id === l1.Id)))
                    );
                },
            },
            "Items/LabelSelection/SelectionCode": {
                customizationData: {
                    useForCustomization: false,
                },
                defaultValue: SelectionCode.None,
            },
        };
    }

    return def;
};

export const getRelevantAccountSide = (storage: FormStorage): "CreditAccount" | "DebitAccount" => {
    const rootEntitySet = storage.data.bindingContext.getRootParent().getPath(true);

    if (rootEntitySet === EntitySetName.CashReceiptsReceived) {
        return "DebitAccount";
    } else if (rootEntitySet === EntitySetName.CashReceiptsIssued) {
        return "CreditAccount";
    }

    // else EntitySetName.BankTransactions
    const bankTransactionType: BankTransactionTypeCode = storage.data.entity[BankTransactionEntity.BankTransactionType]?.Code;

    if (bankTransactionType === BankTransactionTypeCode.IncomingPayment) {
        return "DebitAccount";
    } else {
        return "CreditAccount";
    }
};


/** In BankTransactions and CashReceipts, if user selected custom account assignments,
 * we want to always change it to the one that corresponds with selected BankAccount/CashBox.
 * This function will return the correct account number;
 * @see https://solitea-cz.atlassian.net/browse/DEV-17687*/
export const getFixedAccountAssignmentAccountNumberForPaymentEntity = (bindingContext: BindingContext, entity: IBankTransactionEntity | ICashReceiptEntity): {
    accountPrefix: string,
    accountSuffix: string
} => {
    const path = bindingContext.getRootParent().getPath(true);
    const isCashBox = path === EntitySetName.CashReceiptsReceived || path === EntitySetName.CashReceiptsIssued;
    if (isCashBox) {
        const _entity = (entity as ICashReceiptEntity);
        return {
            accountPrefix: getCashboxPrefix(_entity),
            accountSuffix: _entity.CashBox?.BalanceSheetAccountNumberSuffix
        };
    }

    return {
        accountPrefix: (entity as IBankTransactionEntity).BankStatement?.BankAccountBase?.BalanceSheetAccountNumberPrefix,
        accountSuffix: (entity as IBankTransactionEntity).BankStatement?.BankAccountBase?.BalanceSheetAccountNumberSuffix
    };
};

const getTransactionCurrency = (storage: FormStorage) => {
    return storage.data.entity?.TransactionCurrency?.Code ?? storage.data.entity?.TransactionCurrencyCode;
};

export const refreshDocumentsExRate = async (storage: FormStorage<any, IBankCustomData>, items?: IPaymentDocumentItemEntity[]): Promise<Map<number, number>> => {
    items = items ?? storage.data.entity.Items?.filter((item: IPaymentDocumentItemEntity) => {
        return item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment && isDefined(item.LinkedDocument?.Id);
    });
    const exRates: Map<number, number> = storage.getCustomData().documentsExchangeRates ?? new Map<number, number>();
    const itemsWithoutRate: IPaymentDocumentItemEntity[] = items?.filter((item: IPaymentDocumentItemEntity) => !exRates?.has(item.Id));
    if (itemsWithoutRate?.length > 0) {
        const rates = await getCorrectExchangeRates(itemsWithoutRate.map(item => ({
            id: item.LinkedDocument?.Id,
            date: item.DateAccountingTransaction
        })), storage.context);

        const newRates = new Map([...exRates, ...rates]);
        storage.setCustomData({ documentsExchangeRates: newRates });

        return newRates;
    }

    return exRates;
};

export const correctGainAccountAssignment = (storage: FormStorage, item: IPaymentDocumentItemEntity): void => {
    const linkedDocId = item?.LinkedDocument?.Id;
    if (linkedDocId) {
        const gain = storage.data.entity.Items?.find((_item: IPaymentDocumentItemEntity) => (_item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain || _item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeLoss)
            && _item.LinkedDocument?.Id === linkedDocId);

        if (gain) {
            const propName = gain.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain ? "DebitAccount" : "CreditAccount";
            const isReceived = (storage.data.entity.TransactionAmount || 0) > 0;
            const sourcePropName = isReceived ? "CreditAccount" : "DebitAccount";

            const path = `AccountAssignmentSelection/AccountAssignment/${propName}`;
            const sourcePath = `AccountAssignmentSelection/AccountAssignment/${sourcePropName}`;

            const val = getNestedValue(sourcePath, item);
            setNestedValue(val, path, gain);

            const id = gain.Id ?? `${BindingContext.NEW_ENTITY_ID_PROP}=${gain[BindingContext.NEW_ENTITY_ID_PROP]}`;
            const _path = `Items(${id})/AccountAssignmentSelection/AccountAssignment/${propName}`;
            const bc = storage.data.bindingContext.navigate(_path);
            storage.setValue(bc, val);

            // we need to invalidate whole fast entry as some lines could be hidden (when clicking "no accounting" f.e.)
            storage.addActiveField(storage.data.bindingContext.navigate("Items"));
        }
    }
};

export const calculateExchangeGainFromItem = async (storage: FormStorage, item: IPaymentDocumentItemEntity, amount: number = item?.TransactionAmount) => {
    if (item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment) {
        const linkedId = item?.LinkedDocument?.Id;
        if (linkedId) {
            const doc = item.LinkedDocument;
            const docCurrency = doc.TransactionCurrencyCode;
            const tranCurrency = getTransactionCurrency(storage);

            const rates = await refreshDocumentsExRate(storage, [item]);
            const docExchangeRate = rates.get(linkedId) ?? 1;
            const docType = doc.DocumentTypeCode as DocumentTypeCode;

            const hasExchangeRateDiff = hasExchangeGain(docCurrency, docType, storage.context);
            if (hasExchangeRateDiff) {
                const tranExRate = storage.data.entity.ExchangeRatePerUnit;
                const linkExRate = item.ExchangeRate;

                return calculateExchangeRate({
                    docCurrency,
                    tranCurrency,
                    docExchangeRate,
                    docType,
                    tranExRate,
                    currentAmount: amount,
                    ratedAmount: item.ClearedAmount,
                    currentExRate: linkExRate,
                    type: getTransactionTypeFromEntity(storage),
                    context: storage.context
                });
            }
        }
    }

    return null;
};

export const updateSplitFromBe = async (storage: FormStorage, item: IPaymentDocumentItemEntity) => {
    const entity: IBankTransactionEntity = storage.data.entity;
    const docCurrency = item.LinkedDocument?.TransactionCurrencyCode;
    const docType = item.LinkedDocument?.DocumentTypeCode;

    if (hasExchangeGain(docCurrency, docType as DocumentTypeCode, storage.context)) {
        const splitsRequests: TRecordAny[] = [];
        const choAId = getChoAIdBasedOnDate(storage, item.DateAccountingTransaction);

        for (const splitItem of item.SplitAccountAssignments) {
            splitsRequests.push({
                Amount: splitItem.Amount,
                TransactionAmount: splitItem.TransactionAmount,
                ChartOfAccountsId: choAId,
                DebitAccountNumber: splitItem.DebitAccount?.Number,
                CreditAccountNumber: splitItem.CreditAccount?.Number
            });
        }

        const body = {
            Payment: {
                PaymentDate: entity.DateBankTransaction,
                AccountNumber: entity.BankAccount?.AccountNumber,
                CurrencyCode: entity.CurrencyCode,
                TransactionCurrencyCode: entity.TransactionCurrencyCode,
                ExchangeRatePerUnit: entity.ExchangeRatePerUnit,
                IsDebit: entity.BankTransactionTypeCode === BankTransactionTypeCode.OutgoingPayment,
                IsCredit: entity.BankTransactionTypeCode === BankTransactionTypeCode.IncomingPayment
            },
            PaymentItems: [
                {
                    LinkedDocumentId: item.LinkedDocument?.Id,
                    Amount: item.Amount,
                    TransactionAmount: item.TransactionAmount,
                    ClearedAmount: item.ClearedAmount ?? item.TransactionAmount,
                    DateAccountingTransaction: item.DateAccountingTransaction,
                    AccountAssignmentSelection: {
                        SelectionCode: SelectionCode.Split
                    },
                    SplitAccountAssignments: splitsRequests
                }
            ]
        };

        const url = `${UPDATE_CLEARING_SPLIT_ITEMS}?CompanyId=${storage.context.getCompany().Id}`;

        const response = await fetch(url, {
            ...getDefaultPostParams(),
            body: JSON.stringify(body)
        });

        const results = await response.json();

        if (!response.ok) {
            return parseError(results);
        }

        const gains: IPaymentDocumentItemEntity[] = [];
        const gainsResults = results?.[0]?.ExchangeRateDifferences;

        const items = storage.data.entity.Items.filter((_item: IPaymentDocumentItemEntity) => _item.LinkedDocument?.Id !== item.LinkedDocument.Id
            || _item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment);

        let maxId = getNewItemsMaxId(items) + 1;

        for (const gain of gainsResults || []) {
            const accAss = gain.AccountAssignmentSelection.AccountAssignment;
            gains.push({
                Amount: gain.Amount,
                LinkedDocument: { ...item.LinkedDocument },
                DateAccountingTransaction: item.DateAccountingTransaction,
                AccountAssignmentSelection: {
                    AccountAssignment: {
                        Id: SelectionCode.Own as any as number,
                        ChartOfAccounts: {
                            Id: accAss.ChartOfAccounts.Id
                        },
                        CreditAccount: {
                            Id: accAss.CreditAccount.Id,
                            Name: accAss.CreditAccount.Name,
                            Number: accAss.CreditAccount.Number
                        },
                        DebitAccount: {
                            Id: accAss.DebitAccount.Id,
                            Name: accAss.DebitAccount.Name,
                            Number: accAss.DebitAccount.Number
                        },
                        Name: accAss.Name,
                        ShortName: accAss.ShortName
                    },
                    Selection: {
                        Code: SelectionCode.Own
                    }
                },
                [BindingContext.NEW_ENTITY_ID_PROP]: maxId++,
                PaymentDocumentItemTypeCode: gain.PaymentDocumentItemTypeCode,
                TransactionAmount: gain.TransactionAmount
            });
        }

        storage.data.entity.Items = items.concat(gains);
    }
    return undefined;
};

export const updateSplitGains = async (storage: FormStorage, item: IPaymentDocumentItemEntity): Promise<void> => {
    const items = storage.data.entity.Items.filter((_item: IPaymentDocumentItemEntity) => _item.LinkedDocument?.Id !== item.LinkedDocument.Id
        || _item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment);

    storage.data.entity.Items = items.concat(await getSplitGains(storage, item, getNewItemsMaxId(items) + 1));
};

export const getSplitGains = async (storage: FormStorage, item: IPaymentDocumentItemEntity, maxId: number) => {
    const exRateDiff = await calculateExchangeGainFromItem(storage, item);
    const items: IPaymentDocumentItemEntity[] = [];

    if (exRateDiff) {
        const rowAmount = item?.TransactionAmount;

        for (let i = 0; i < (item.SplitAccountAssignments || []).length; i++) {
            const split = item.SplitAccountAssignments[i];
            const rate = split.TransactionAmount / rowAmount;
            const amount = roundToDecimalPlaces(2, exRateDiff * rate);

            const gain = await createNewGain({
                item,
                amount,
                storage,
                newMax: maxId + i,
                splitItem: split,
                date: item.DateAccountingTransaction
            });

            items.push(gain);
        }
    }

    return items;
};

export const getCorrectTranAmount = (storage: FormStorage, docId: number, docAmount: number, docCurrency: string, isIDClearing?: boolean): number => {
    // status 'Needs Approval' indicates document is not yet posted by the transaction value so skip this routine
    // can be called for internal document too, but it is undefined then and we can skip it
    const needsApproval = storage.data.entity.PaymentStatusCode === PaymentStatusCode.NeedsApproval;
    if (needsApproval) {
        return docAmount;
    }

    const entity: IBankTransactionEntity = storage.data.origEntity;
    const paired = entity.Items?.find(item => item.LinkedDocument?.Id === docId &&
        // internal document does not have documentItemTypeCode so we check for undefined
        (isNotDefined(item.PaymentDocumentItemTypeCode) || item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment));

    if (paired) {
        // Cleared amount -- ALWAYS in document currency todo: not always ID clearing on cba company
        // this should clear cases with different NON CZK currencies (EUR-$)
        // internal document item doesn't have cleared amount, so we fill local context ORIGINAL_CLEARED_AMOUNT_PATH from document link with right amount,
        // if it is not present (eg. new item) we use TransactionAmount
        const clearedAmount = isIDClearing ? (paired as IRegularDocumentItemExtendedEntity)[ORIGINAL_CLEARED_AMOUNT_PATH] ?? paired.TransactionAmount : paired.ClearedAmount;
        if (clearedAmount) {
            return docAmount + clearedAmount;
        }

        return docAmount + (docCurrency === getCompanyCurrency(storage.context) ? paired.Amount : paired.TransactionAmount);
    }

    return docAmount;
};

export const handleItemAmountBlur = async (storage: FormStorage, args: ISmartFieldBlur): Promise<void> => {
    const path = args.bindingContext.getPath();

    if (path === "Amount" || path === "TransactionAmount" || path === "ExchangeRatePerUnit") {
        storage.addActiveField(storage.data.bindingContext.navigate("Items"));
    }
};

interface ICorrectExchangeRateData {
    id: number;
    date: Date;
    currency?: string;
}

export const getCorrectExchangeRate = async (data: ICorrectExchangeRateData, context: IAppContext): Promise<number> => {
    const rates = await getCorrectExchangeRates([data], context);
    return rates?.get(data.id);
};

export const getCorrectExchangeRates = async (data: ICorrectExchangeRateData[], context: IAppContext): Promise<Map<number, number>> => {
    const rates = new Map<number, number>();
    const companyCurrency = getCompanyCurrency(context);
    const formattedData = data?.filter(item => {
        if (item.currency === companyCurrency) {
            rates.set(item.id, 1); // set rate to 1 for company currency
            return false; // no need to fetch rates for company currency
        }
        return true;
    }).map(item => {
        return {
            DocumentId: item.id,
            DateTransaction: formatDateToDateString(item.date)
        };
    });

    if (formattedData?.length > 0) {
        const response = await customFetch(`${REST_API_URL}/ExchangeRate/RatesForDocuments`, {
            ...getDefaultPostParams(),
            body: JSON.stringify({ DocumentDatesPair: formattedData })
        });

        const results = await response.json();
        for (const res of results?.DocumentRatesPair || []) {
            rates.set(res.DocumentId, res.ExchangeRatePerUnit);
        }
    }

    return rates;
};

export const itemHasAssignment = (item: IPaymentDocumentItemEntity, items: IPaymentDocumentItemEntity[]): boolean => {
    const docId = item.LinkedDocument?.Id;
    const payment = items.find(_i => _i.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment && _i.LinkedDocument?.Id === docId);
    return !(payment?.AccountAssignmentSelection?.Selection?.Code === SelectionCode.None);
};

export const addExpenseGainIfMissing = async (storage: FormStorage, item: IPaymentDocumentItemEntity): Promise<void> => {
    const gains = getExpenseGains(storage, item?.LinkedDocument?.Id);
    if (!gains?.length) {
        const amount = await calculateExchangeGainFromItem(storage, item);
        if (amount) {
            const gain = await createNewGain({
                item,
                amount,
                storage,
                newMax: getNewItemsMaxId(storage.data.entity.Items) + 1,
                date: item.DateAccountingTransaction
            });

            storage.data.entity.Items.push(gain);
            storage.refresh();
        }
    }
};

export const deleteAdditionalGains = async (storage: FormStorage, item: IPaymentDocumentItemEntity, oldType: TValue) => {
    if (oldType === SelectionCode.Split) {
        const linkedDocId = item?.LinkedDocument?.Id;

        let foundPayment = false;

        // a bit clunky, what we want is to make sure there is only one Linked Document loss(gain) row
        storage.data.entity.Items = storage.data.entity.Items?.filter((_item: IPaymentDocumentItemEntity) => {
            const isLinkedPayemt = (_item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain
                || _item.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeLoss) && _item.LinkedDocument?.Id === linkedDocId;

            let result = true;
            if (isLinkedPayemt) {
                result = !foundPayment;
                foundPayment = true;
            }

            return result;
        });

        // recalculate
        const gain = getExpenseGains(storage, item?.LinkedDocument?.Id)[0];
        if (!gain) {
            await addExpenseGainIfMissing(storage, item);
        } else {
            const amount = await calculateExchangeGainFromItem(storage, item);
            const currency = storage.data.entity?.TransactionCurrency?.Code;

            gain.Amount = amount;
            gain.TransactionAmount = currency === getCompanyCurrency(storage.context) ? amount : 0;
        }
    }
};

export const handleBankAssignmentChange = async (args: ISmartFieldChange, storage: FormStorage): Promise<void> => {
    if (args.bindingContext?.getPath() === "AccountAssignment") {
        const { bindingContext } = args;
        const itemBc = bindingContext.getParent().getParent();
        const item = storage.getValue(itemBc);

        const oldType = item?.AccountAssignmentSelection?.Selection?.Code;

        handleItemAccAssignmentChange({
            storage,
            e: args
        });

        const items = storage.data.entity.Items;

        await deleteAdditionalGains(storage, item, oldType);
        correctGainAccountAssignment(storage, item);

        // clear all gains and losses assigned to changed pair
        if (args.value === SelectionCode.None) {
            storage.data.entity.Items = items.filter((_item: IPaymentDocumentItemEntity) => {
                if (isPaymentOrOther(_item.PaymentDocumentItemTypeCode)) {
                    return true;
                }

                return item.LinkedDocument?.Id !== _item.LinkedDocument?.Id;
            });

            storage.refresh();
        }

        if (args.value !== SelectionCode.None) {
            addExpenseGainIfMissing(storage, item);
        }
    }
};

export const isPaymentOrOther = (type: string): boolean => {
    return type === PaymentDocumentItemTypeCode.Other || type === PaymentDocumentItemTypeCode.Payment;
};

interface IPairDataForRow {
    originalTransactionAmount: number;
    amounts: TRecordAny;
    docAmount: number;
    docExchangeRate: number;
    docCurrency: string;
    tranExRate: number;
    tranCurrency: string;
    defaultExchangeRate: number;
    tranType: BankTransactionTypeCode;
    docType: DocumentTypeCode;
}

export const getPairDataForRow = (args: IPairDataForRow, context: IAppContext) => {

    // Transaction Amount - already paid amount
    const transAmount = args.originalTransactionAmount - args.amounts.transactionAmount;

    let amount, transactionAmount;
    // we convert different currencies to CZK (or system) currency and then we compare them
    const docAmountCZK = args.docAmount * args.docExchangeRate;
    const transAmountCZK = transAmount * args.tranExRate;

    if (args.docCurrency !== args.tranCurrency) {
        let exchangedDocAmount = formatNumberCurrency(docAmountCZK / args.tranExRate);
        const reverted = formatNumberCurrency(exchangedDocAmount * args.tranExRate);

        if (reverted > docAmountCZK) {
            // with trunc we make sure truncated amount is always lower then the doc amount
            // f.e. 1$ ->25.888CZK should not make 25.89 as in revert it would make 1.01$
            // we are fine with 25.88 and a bit in exchange rate gain(loss)

            exchangedDocAmount = Math.trunc(docAmountCZK / args.tranExRate * 100) / 100;
        }


        if (docAmountCZK < transAmountCZK) {
            amount = docAmountCZK;
            transactionAmount = exchangedDocAmount;
        } else {
            amount = transAmountCZK;
            transactionAmount = transAmount;
        }
    } else {
        const value = Math.min(transAmount, args.docAmount);
        amount = formatNumberCurrency(value * args.tranExRate);
        transactionAmount = value;
    }

    const _hasExchangeGain = hasExchangeGain(args.docCurrency, args.docType, context);
    const _hasAdditionalExRate = hasAdditionalExRate(args.docCurrency, args.tranCurrency, context);

    let exchangeGain = null;
    let ratedAmount;
    if (_hasAdditionalExRate && args.defaultExchangeRate) {
        ratedAmount = formatNumberCurrency(transactionAmount * args.tranExRate / args.defaultExchangeRate);
        if (docAmountCZK < transAmountCZK) {
            ratedAmount = formatNumberCurrency(args.docAmount);
            amount = formatNumberCurrency(ratedAmount * args.defaultExchangeRate);
            transactionAmount = formatNumberCurrency(amount / (args.tranExRate ?? 1));
        }

        exchangeGain = calculateExchangeRate({
            docCurrency: args.docCurrency,
            tranCurrency: args.tranCurrency,
            tranExRate: args.tranExRate,
            docExchangeRate: args.docExchangeRate,
            ratedAmount,
            docType: args.docType,
            currentExRate: args.defaultExchangeRate,
            currentAmount: transactionAmount,
            type: args.tranType,
            context
        });
    } else if (_hasExchangeGain) {
        exchangeGain = calculateExchangeRate({
            docCurrency: args.docCurrency,
            tranCurrency: args.tranCurrency,
            tranExRate: args.tranExRate,
            docType: args.docType,
            docExchangeRate: args.docExchangeRate,
            currentAmount: transactionAmount,
            type: args.tranType,
            context
        });
    }

    return {
        amount,
        transactionAmount,
        exchangeGain,
        ratedAmount
    };
};

export enum ValidateMaxType {
    Amount = "amount",
    Gain = "gain"
}

export interface IValidateMaxArgs {
    type: ValidateMaxType;
    amount: number;
    docAmount: number;
    tranExRate: number;
    docExRate: number;
    docCurrency: string;
    tranCurrency: string;
    tranAmount: number;

    clearingByID?: boolean;
    t?: TFunction;
}

export const validateMaxData = (args: IValidateMaxArgs, context: IAppContext) => {
    if (args.type === ValidateMaxType.Amount) {
        return validateMaxAmount(args, context);
    }

    if (args.type === ValidateMaxType.Gain) {
        // for gain we only validate document amount as transaction is validated for "amount"
        return validateMaxDocAmount(args, context);
    }

    return null;
};

const validateMaxAmount = (args: IValidateMaxArgs, context: IAppContext) => {
    if (args.clearingByID) {
        return validateMaxDocAmount(args, context);
    }

    // if there is additional exchange rate row
    // we validate only against transaction
    const hasAddExRate = hasAdditionalExRate(args.docCurrency, args.tranCurrency, context);
    if (hasAddExRate) {
        return validateMaxTranAmount(args);
    }

    // otherwise we validate both document transaction
    return validateMaxDocAmount(args, context) || validateMaxTranAmount(args);
};

const validateMaxDocAmount = (args: IValidateMaxArgs, context: IAppContext) => {
    // can be called for either amount or gain validation (amount vs rated amount)
    // so this condition covers both cases
    const withoutExRate = hasAdditionalExRate(args.docCurrency, args.tranCurrency, context) || args.docCurrency === args.tranCurrency;
    const amount = roundToDecimalPlaces(2, args.amount * (withoutExRate ? 1 : args.tranExRate));
    const docAmount = roundToDecimalPlaces(2, args.docAmount * (withoutExRate ? 1 : args.docExRate));

    if (Math.abs(amount) > Math.abs(docAmount)) {
        return {
            message: args.t?.("Banks:Pairing.AmountToHigh"),
            errorType: ValidationErrorType.Field
        };
    }

    return null;
};

const validateMaxTranAmount = (args: IValidateMaxArgs) => {
    const amount = args.amount;
    const transactionAmount = Math.abs(args.tranAmount);
    if (amount > transactionAmount) {
        return {
            message: args.t?.("Banks:Pairing.AmountToHighTran"),
            errorType: ValidationErrorType.Field
        };
    }

    return null;
};

export const createPairColumns = (context: IAppContext, addLinkedDoc = true, addGrant = true, includeLabels = false): IFieldDef[] => {
    let columns = [{
        id: "TransactionAmount"
    }];

    if (addLinkedDoc) {
        columns.push({
            id: "LinkedDocument"
        });

        if (shouldAddGrantToDefaultFields(context) && addGrant) {
            columns.push({id: createPath(DocumentItemEntity.GrantAssignment, DocumentItemGrantAssignmentEntity.Grant)});
        }
    }

    if (!isCashBasisAccountingCompany(context)) {
        columns = arrayInsert(columns, { id: "Amount" }, 1);
        columns = arrayInsert(columns, { id: "AccountAssignmentSelection/AccountAssignment" }, 2);
        columns = arrayInsert(columns, { id: "DateAccountingTransaction" }, 2);
    } else {
        columns = arrayInsert(columns, { id: CBA_CATEGORY_PATH }, 1);
    }

    if (includeLabels) {
        columns.push({ id: "LabelSelection/Labels" });
    }

    return columns;
};


interface IBankFormatterArgs {
    amount: number;
    transactionAmount?: number;
    currencyCode: string;
    transactionCurrencyCode?: string;
    useColor?: boolean;
    showPositive?: boolean;
    isPositive?: boolean;
}


const _bankFormatter = (args: IBankFormatterArgs): ICellValueObject => {
    const _convert = (val: number) => {
        return args.showPositive ? Math.abs(val) : val;
    };

    const hasDiffCurrency = args.transactionCurrencyCode !== args.currencyCode;
    const color: keyof DefaultTheme = args.useColor ? args.isPositive !== false && args.transactionAmount > 0 ? "C_SEM_text_good" : "C_TEXT_primary" : null;

    const formattedTransactionAmount = formatCurrency(_convert(args.transactionAmount) || 0, hasDiffCurrency ? args.transactionCurrencyCode : args.currencyCode);

    if (args.transactionCurrencyCode && hasDiffCurrency) {
        const formattedAmount = formatCurrency(_convert(args.amount) || 0, args.currencyCode);

        return {
            value: <b><ColoredText color={color}><SmallText>{formattedAmount} | </SmallText>
                <BoldText>{formattedTransactionAmount}</BoldText></ColoredText></b>,
            tooltip: `${formattedAmount} | ${formattedTransactionAmount}`
        };
    } else {
        return {
            tooltip: formattedTransactionAmount,
            value: <b><ColoredText color={color}> {formattedTransactionAmount} </ColoredText></b>
        };
    }
};

export interface IPairTableViewBaseProps extends ITableViewBaseProps<IBankCustomData>, WithPermissionContext, WithBusyIndicator, WithConfirmationDialog {
    saveDraftFn?: () => Promise<void>;
    skipAmountPair?: boolean;
    isSingleSelect?: boolean;
    transactionType?: BankTransactionTypeCode;
}

export interface IPaymentDocumentItemEntityExpanded extends IPaymentDocumentItemEntity {
    ExchangeGain?: number;
    Description?: string;
}

const handleClickAmount = async (e: React.MouseEvent, storage: Model, item: TRecordAny) => {
    e.preventDefault();

    const row = (storage as TSmartODataTableStorage).tableAPI.getRowsArray()?.find(row => row.dataId == item.Id);

    storage.setCustomData({
        amountDialogRow: row
    });

    storage.refresh();
};


export const amountDialogLinkFormatter = (value: React.ReactNode, storage: TSmartODataTableStorage<IBankCustomData>, item: TRecordAny): ICellValueObject => {
    const pairedDocuments = storage.getCustomData().pairedDocuments;
    const id = item.Id;
    const pairedLink = pairedDocuments?.[id];
    const isPaired = !!pairedLink;
    const isPairActionActive = isPairingActive(storage as TableStorage);

    const isReadOnly = isPairingRowReadOnly(pairedLink, storage, storage.getCustomData().rootStorage);
    const Icon = !isReadOnly ? EditSimpleIcon : null;

    return {
        tooltip: "",
        value: (
            <LinkStyledBasic
                isPaired={isPaired}
                onClick={(e: React.MouseEvent) => {
                    if (isPaired) {
                        handleClickAmount(e, storage, item);
                    }
                }}>
                {isPaired && isPairActionActive && Icon &&
                    <Icon width={IconSize.XS}/>
                }
                <LinkText>
                    {value}
                </LinkText>
            </LinkStyledBasic>
        )
    };
};

const bankAmountPaidFormatter = (value: TValue, args: IGetValueArgs): ICellValueObject => {
    const storage = args.storage as TSmartODataTableStorage<IBankCustomData>;
    const pairedDocuments = storage.getCustomData().pairedDocuments;
    const id = args.item.Id;
    const pairedLink = pairedDocuments?.[id];
    const amount = pairedLink?.Amount;
    const transactionAmount = pairedLink?.TransactionAmount;

    const rootStorage = storage.getCustomData().rootStorage as FormStorage<any>;
    const docCurrency = args.item.Currency?.Code;
    const transactionCurrency = rootStorage.data.entity.TransactionCurrency?.Code ?? rootStorage.data.entity.TransactionCurrencyCode;

    return _bankFormatter({
        amount,
        transactionAmount,
        transactionCurrencyCode: transactionCurrency,
        currencyCode: docCurrency,
        useColor: false
    });
};


export const amountPaidFormatter = (val: TValue, args: IFormatOptions): ICellValueObject => {
    const value = bankAmountPaidFormatter(val, args)?.value;
    return amountDialogLinkFormatter(value, args.storage as TSmartODataTableStorage<IBankCustomData>, args.item);
};

export const docOnlyAmountPaidFormatter = (val: TValue, args: IFormatOptions): ICellValueObject => {
    const storage = args.storage as TSmartODataTableStorage<IBankCustomData>;
    const pairedDocuments = storage.getCustomData().pairedDocuments;
    const id = args.item.Id;
    const pairedLink = pairedDocuments?.[id];
    const amount = pairedLink?.Amount ?? 0;

    const formattedAmount = formatCurrency(amount, getCompanyCurrency(args.storage.context));

    const value = <b><ColoredText color={null}> {formattedAmount} </ColoredText></b>;
    return amountDialogLinkFormatter(value, storage, args.item);
};


const NON_PROFIT_GAIN_ACC = "645";
const NON_PROFIT_LOSS_ACC = "545";
const PROFIT_GAIN_ACC = "663";
const PROFIT_LOSS_ACC = "563";


export const hasPairedChanged = (storage: FormStorage, changedData: TRecordAny): boolean => {
    const pairedOrigData = (storage.data.entity.Items || []).filter((link: IPaymentDocumentItemEntity) => {
        return link.LinkedDocument?.Id && link.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.Payment;
    });

    for (const item of pairedOrigData) {
        const id = item?.LinkedDocument?.Id;
        const origItem = changedData[id];
        if (!origItem || origItem.Amount !== item.Amount) {
            return true;
        }
    }

    return Object.keys(changedData)?.length !== pairedOrigData.length;
};

interface IGetExpenseTitleOptions {
    width?: string;
    withMargin?: boolean;
}

export const getExpenseTitle = (isLoss: boolean, options: IGetExpenseTitleOptions = {}): React.ReactNode => {
    const text = i18next.t(`Banks:Pairing.${isLoss ? "ExchangeRateLoss" : "ExchangeRateGain"}`);
    const Icon = isLoss ? GrowthNegativeIcon : GrowthPositiveIcon;

    return (
        <GainWrapper data-testid={TestIds.GainRowLabel} $width={options.width} $withMargin={options.withMargin}
                     data-elementtype="FastEntryCol">
            <IconWrapper>
                <Icon status={isLoss ? Status.Error : Status.Success}
                      width={IconSize.XS}/></IconWrapper>
            <ExchangeRateDiffLabel isLoss={isLoss}>{text}</ExchangeRateDiffLabel>
        </GainWrapper>
    );
};

interface ICreateGainArgs {
    gain?: IPaymentDocumentItemEntity;
    item: IPaymentDocumentItemEntity;
    splitItem?: ISplitAccountAssignmentEntity;
    amount: number;
    storage: FormStorage;
    newMax: number;
    date?: Date;
}

export const createNewGain = async (args: ICreateGainArgs) => {
    const tValue = Math.abs(args.amount);

    const currency = args.storage.data.entity?.TransactionCurrency?.Code;
    if (args.gain) {
        args.gain.PaymentDocumentItemTypeCode = args.amount > 0 ? PaymentDocumentItemTypeCode.ExchangeGain : PaymentDocumentItemTypeCode.ExchangeLoss;
        args.gain.LinkedDocument = { ...args.item.LinkedDocument };
    } else {
        args.gain = BindingContext.createNewEntity<IPaymentDocumentItemEntity>(args.newMax ?? 1, {
            PaymentDocumentItemTypeCode: args.amount > 0 ? PaymentDocumentItemTypeCode.ExchangeGain : PaymentDocumentItemTypeCode.ExchangeLoss,
            DateAccountingTransaction: args.item.DateAccountingTransaction,
            LinkedDocument: { ...args.item.LinkedDocument }
        });
    }

    args.gain.Amount = tValue;
    args.gain.TransactionAmount = currency === getCompanyCurrency(args.storage.context) ? args.gain.Amount : 0;

    const isGain = args.gain.PaymentDocumentItemTypeCode === PaymentDocumentItemTypeCode.ExchangeGain;

    const accounts = await loadAccountsBasedOnDate(args.storage, args.date ?? getUtcDate());
    const type = getTransactionTypeFromEntity(args.storage);
    const isIncoming = type === BankTransactionTypeCode.IncomingPayment;
    const sourcePropName = isIncoming ? "CreditAccount" : "DebitAccount";

    const propName = isGain ? "DebitAccount" : "CreditAccount";
    const propName1 = isGain ? "CreditAccount" : "DebitAccount";

    const isNonProfit = isNonProfitCompany(args.storage.context);
    const defVal = isNonProfit ? (isGain ? NON_PROFIT_GAIN_ACC : NON_PROFIT_LOSS_ACC) : (isGain ? PROFIT_GAIN_ACC : PROFIT_LOSS_ACC);

    const defAcc = accounts.find((obj: TRecordAny) => isMatchingAccount(obj, { Number: defVal }, true));
    if (defAcc) {
        setNestedValue({
            Id: defAcc.Id,
            Number: defAcc.Number,
            Name: defAcc.Name
        }, `AccountAssignmentSelection/AccountAssignment/${propName1}`, args.gain);
    }

    const prefix = args.splitItem ? "" : "AccountAssignmentSelection/AccountAssignment/";
    const sourcePath = `${prefix}${sourcePropName}`;
    const acc = getNestedValue(sourcePath, args.splitItem ?? args.item);

    const path = `AccountAssignmentSelection/AccountAssignment/${propName}`;
    if (acc) {
        setNestedValue({
            Id: acc.Id,
            Name: acc.Name,
            Number: acc.Number
        }, path, args.gain);
    }

    setNestedValue(SelectionCode.Own, "AccountAssignmentSelection/AccountAssignment/Id", args.gain);
    setNestedValue(SelectionCode.Own, "AccountAssignmentSelection/Selection/Code", args.gain);

    const name = isGain ? i18next.t("Banks:Pairing.ExchangeGain") : i18next.t("Banks:Pairing.ExchangeLoss");
    setNestedValue(name, "AccountAssignmentSelection/AccountAssignment/Name", args.gain);

    return args.gain;
};

export const loadCreditData = async (storage: FormStorage) => {
    const caInfo = getInfoByPath(storage, `Items/AccountAssignmentSelection/AccountAssignment/CreditAccount`),
        daInfo = getInfoByPath(storage, `Items/AccountAssignmentSelection/AccountAssignment/DebitAccount`);

    const cas = caInfo.fieldSettings.items;
    const das = daInfo.fieldSettings.items;

    const promises = [];

    if (!cas) {
        promises.push(fetchAndSetItemsByInfo(storage, caInfo));
    }

    if (!das) {
        promises.push(fetchAndSetItemsByInfo(storage, daInfo));
    }

    const [_cas, _das] = await Promise.all(promises);

    return {
        cas: cas || _cas || [],
        das: das || _das || []
    };
};

export const getAccNumberFromPair = (pair: IPaymentDocumentItemEntityExpanded, isReceived: boolean) => {
    const accName = isReceived ? "CreditAccount" : "DebitAccount";
    return Number(getNestedValue(`AccountAssignmentSelection/AccountAssignment/${accName}/Number`, pair));
};


export const getRoundedSplitSum = (item: IPaymentDocumentItemEntity, storage: FormStorage) => {
    const sum = item.SplitAccountAssignments?.reduce((accumulator: number, object: ISplitAccountAssignmentEntity) => {
        return accumulator + (object.TransactionAmount || 0);
    }, 0) || 0;

    const maximumFractionDigits = storage.data.entity.TransactionCurrency?.MinorUnit ?? 2;
    return roundToDecimalPlaces(maximumFractionDigits, sum);
};

enum DocumentItemsType {
    None,
    Single,
    Split
}

interface ICreateSplitItemsAndAccountNumberArgs {
    documentType: DocumentTypeCode;
    key: number;
    storage: FormStorage;
    isReceived: boolean;
    transactionType: BankTransactionTypeCode;
    isTaxDocument: boolean;
    isBank: boolean;
    transactionDate: Date;
    pairedDoc: IPaymentDocumentItemEntityExpanded;
    maxId: number;
}

export const getCashboxPrefix = (entity: Record<string, any>) => {
    if(!("CashBox" in entity)) {
        console.error("CashBox property is missing in entity, fallback to default prefix");
    }
    return entity?.CashBox?.BalanceSheetAccountNumberPrefix ?? DEFAULT_CASH_BOXES_BALANCE_SHEET_ACCOUNT_PREFIX;
};

export const getFixAccountNumber = (storage: FormStorage, isBank: boolean) => {
    let prefix = "";
    if (isBank) {
        const banks = storage.context.getCompanyBankAccounts();
        const currentBankId = storage.data.entity.BankStatement.BankAccountBase.Id;
        const currentBank = banks.find(bank => bank.Id === currentBankId);
        prefix = currentBank.BalanceSheetAccountNumberPrefix;
    } else {
        prefix = getCashboxPrefix(storage.data?.entity);
    }

    const suffix = _getAccountSuffix(storage);
    return `${prefix}${suffix}`;
};


export const createOrUpdatePair = async (args: ICreateSplitItemsAndAccountNumberArgs) => {
    const _getCa = (item: IEntity) => {
        const selectionCode = item.AccountAssignmentSelection.SelectionCode;
        if (selectionCode === SelectionCode.Default) {
            return document.AccountAssignmentSelection?.AccountAssignment?.[loadAccName]?.Number;
        }

        if (selectionCode === SelectionCode.Own || selectionCode === SelectionCode.Copy) {
            return item.AccountAssignmentSelection?.AccountAssignment?.[loadAccName]?.Number;
        }
    };

    const _getDocItemsType = (document: IEntity) => {
        let type = DocumentItemsType.None;
        const items = document?.Items || [];

        for (const item of items) {
            const itemNumber = _getCa(item);
            if (assNumber !== itemNumber) {
                if (assNumber) {
                    type = DocumentItemsType.Split;
                    break;
                }

                type = DocumentItemsType.Single;
            }

            assNumber = itemNumber;
        }

        return type;
    };

    const _loadDoc = async (): Promise<IEntity> => {
        const entitySet = getEntitySetByDocumentType(args.documentType);
        const query = args.storage.oData.getEntitySetWrapper(entitySet)
            .query(args.key)
            .expand(DocumentEntity.AccountAssignmentSelection,
                q => q.expand(DocumentAccountAssignmentSelectionEntity.AccountAssignment, q => q.expand(loadAccName)))
            .expand(PaymentDocumentEntity.Items,
                q => q.expand(DocumentEntity.AccountAssignmentSelection,
                    q => q.expand(DocumentAccountAssignmentSelectionEntity.AccountAssignment, q => q.expand(loadAccName)))
            );

        const result = await query.fetchData<IEntity[]>();
        return result?.value as IEntity;
    };

    const _manageSingleAccount = async () => {
        const accounts = await loadAccountsBasedOnDate(args.storage, args.transactionDate);
        const fixNumberAcc = getFixAccountNumber(args.storage, args.isBank);
        const fixAcc = accounts?.find(a => isMatchingAccount(a, { Number: fixNumberAcc }, true));
        const account = accounts?.find(a => isMatchingAccount(a, { Number: assNumber?.toString() }, true));

        if ((args.isReceived && fixAcc) || (!args.isReceived && account)) {
            const fixPath = createPath(PaymentDocumentItemEntity.AccountAssignmentSelection, PaymentDocumentItemAccountAssignmentSelectionEntity.AccountAssignment, fixAccName);
            const path = createPath(PaymentDocumentItemEntity.AccountAssignmentSelection, PaymentDocumentItemAccountAssignmentSelectionEntity.AccountAssignment, setAccName);

            if (fixAcc && account) {
                setNestedValue({
                    Id: fixAcc.Id,
                    Number: fixAcc.Number,
                    Name: fixAcc.Name
                }, fixPath, pair);

                setNestedValue({
                    Id: account.Id,
                    Number: account.Number,
                    Name: account.Name
                }, path, pair);

                pair.AccountAssignmentSelection.AccountAssignment.Name = args.transactionType === BankTransactionTypeCode.IncomingPayment ?
                    args.storage.t("Banks:Pairing.PaymentIncoming") : args.storage.t("Banks:Pairing.PaymentOutgoing");

                pair.AccountAssignmentSelection.AccountAssignment.ShortName = `${pair.AccountAssignmentSelection.AccountAssignment.DebitAccount?.Number} / ${pair.AccountAssignmentSelection.AccountAssignment.CreditAccount?.Number}`;

                setNestedValue(SelectionCode.Own,
                    createPath(PaymentDocumentItemEntity.AccountAssignmentSelection, PaymentDocumentItemAccountAssignmentSelectionEntity.AccountAssignment, AccountAssignmentEntity.Id), pair);
                setNestedValue(SelectionCode.Own,
                    createPath(PaymentDocumentItemEntity.AccountAssignmentSelection, PaymentDocumentItemAccountAssignmentSelectionEntity.Selection, SelectionEntity.Code), pair);
            } else {
                setNestedValue(null, path, pair);
                setNestedValue(null, fixPath, pair);
            }
        }
    };

    const _manageSplitAccount = async () => {
        splitRequestData = {
            DocumentId: args.key,
            Amount: args.pairedDoc.Amount,
            TransactionAmount: args.pairedDoc.TransactionAmount,
            ClearedAmount: args.pairedDoc.ClearedAmount ?? args.pairedDoc.TransactionAmount,
            ClearingDate: args.transactionDate
        };

        setNestedValue(SelectionCode.Split, "AccountAssignmentSelection/AccountAssignment/Id", pair);
        setNestedValue(SelectionCode.Split, "AccountAssignmentSelection/Selection/Code", pair);
    };


    // !! LEGEND !!
    // debit/credit, MD/D
    // Incoming Payment => 221.../X where X can be both based on document AR, InvoiceReceived = Credit, InvoiceIssued = Debit
    // Outgoing payment => X/221.... where X is the same is in incoming payment

    const fixAccName = args.transactionType === BankTransactionTypeCode.IncomingPayment ? PaymentDocumentItemAccountAssignmentEntity.DebitAccount : PaymentDocumentItemAccountAssignmentEntity.CreditAccount;
    const setAccName = args.transactionType === BankTransactionTypeCode.IncomingPayment ? PaymentDocumentItemAccountAssignmentEntity.CreditAccount : PaymentDocumentItemAccountAssignmentEntity.DebitAccount;
    const loadAccName = args.isReceived ? PaymentDocumentItemAccountAssignmentEntity.CreditAccount : PaymentDocumentItemAccountAssignmentEntity.DebitAccount;

    const hasAccountAssignment = isAccountAssignmentCompany(args.storage.context);
    const isNew = !isDefined(args.pairedDoc?.Id) && !isDefined(args.pairedDoc?.[BindingContext.NEW_ENTITY_ID_PROP]);
    const pair: IEntity = isNew ? BindingContext.createNewEntity(args.maxId, args.pairedDoc) : args.pairedDoc;
    const pairedDoc: IEntity = args.pairedDoc;
    const isChanged = isNew || (pairedDoc.OrigData?.Amount !== pairedDoc.Amount || pairedDoc.OrigData?.ClearedAmount !== pairedDoc.ClearedAmount);

    let type = DocumentItemsType.None;

    let splitRequestData: IEntity;

    let assNumber = 0;
    let document: IEntity;

    // if it is not newly created pair we don't overwrite it, user may change it
    if (isChanged) {
        if (!args.isTaxDocument && (args.documentType === DocumentTypeCode.ProformaInvoiceIssued || args.documentType === DocumentTypeCode.ProformaInvoiceReceived)) {
            assNumber = args.documentType === DocumentTypeCode.ProformaInvoiceReceived ? BANK_ACCOUNT_PROFORMA_RECEIVED : BANK_ACCOUNT_PROFORMA_ISSUED;
            type = DocumentItemsType.Single;
        } else {
            document = await _loadDoc();
            type = _getDocItemsType(document);
        }

        // split account has to be calculated every time because it operates with number and they can be changed in dialog
        if (hasAccountAssignment) {
            switch (type) {
                case DocumentItemsType.Single:
                    await _manageSingleAccount();
                    break;

                case DocumentItemsType.Split:
                    await _manageSplitAccount();
                    break;
            }
        }
    }

    const scale = args.storage.data.entity.TransactionCurrency?.MinorUnit ?? 2;

    pair.PaymentDocumentItemTypeCode = PaymentDocumentItemTypeCode.Payment;
    pair.TransactionAmount = roundToDecimalPlaces(scale, pairedDoc.TransactionAmount);
    pair.Amount = roundToDecimalPlaces(scale, pairedDoc.Amount);
    pair.ClearedAmount = pairedDoc.ClearedAmount;
    pair.ExchangeRate = pairedDoc.ExchangeRate;
    pair.LinkedDocument.ExchangeRatePerUnit = pairedDoc.LinkedDocument.ExchangeRatePerUnit;
    if (hasAccountAssignment) {
        pair.DateAccountingTransaction = pairedDoc.DateAccountingTransaction ?? getUtcDate();
    }

    let gains: IPaymentDocumentItemEntity[];

    if (isChanged) {
        const exchangeGain = pairedDoc.ExchangeGain;
        const hasAssignment = !!pair.AccountAssignmentSelection?.AccountAssignment?.CreditAccount?.Id;
        !!pair.AccountAssignmentSelection?.AccountAssignment?.DebitAccount?.Id;

        if (hasAssignment && type === DocumentItemsType.Single && exchangeGain && !isCashBasisAccountingCompany(args.storage.context)) {
            gains = [await createNewGain({
                item: pair,
                gain: pair.Gains?.[0],
                amount: exchangeGain,
                storage: args.storage,
                newMax: args.maxId + 1,
                date: pair?.DateAccountingTransaction ?? getBankItemDefaultDateAccountingTransaction(args.storage)
            })];
        }
    } else {
        gains = pairedDoc.Gains;
    }

    delete pair.ExchangeGain;
    delete pair.IsTaxDocument;
    delete pair.Gains;
    delete pair.OrigData;

    return { pair, gains, splitRequestData };
};

const _getAccountSuffix = (storage: FormStorage) => {
    return storage.data.entity.BankStatement?.BankAccountBase?.BalanceSheetAccountNumberSuffix ?? storage.data.entity?.CashBox?.BalanceSheetAccountNumberSuffix;
};


const getBusinessPartnerData = (items: IPaymentDocumentItemEntity[]) => {
    let bpId: number = undefined;
    for (const item of items) {
        const currentBpId = item?.LinkedDocument?.BusinessPartner?.BusinessPartner?.Id;
        if (currentBpId && currentBpId !== bpId) {
            if (bpId) {
                return {
                    Id: undefined,
                    isSet: true,
                };
            }

            bpId = currentBpId;
        }
    }

    return {
        Id: bpId,
        isSet: isDefined(bpId) ? true : false
    };
};

export const correctBpForLinkedDocument = async (storage: FormStorage, entity: IBankTransactionEntity, items: IPaymentDocumentItemEntity[]) => {
    const bpData = getBusinessPartnerData(items);
    if (isDefined(bpData.Id)) {
        const bp = await loadPartner(bpData.Id.toString(), storage.oData);
        if (bp) {
            storage.processDependentField(businessPartnerDependentFields(), bp, storage.data.bindingContext);
            setSavedContactItems(storage, bp.Contacts);
        }
    } else {
        if (bpData.isSet) {
            entity.BusinessPartner = null;
            setSavedContactItems(storage, []);
        }
    }
};

export const savePairChanges = async (storage: FormStorage, pairedDocs: Record<TEntityKey, IPaymentDocumentItemEntityExpanded>, type: BankTransactionTypeCode) => {
    const entity = storage.data.entity as IBankTransactionEntity;

    if (!entity.Items) {
        entity.Items = [];
    }

    const entityType = storage.data.bindingContext?.getEntityType()?.getName?.();
    const isBank = entityType === EntityTypeName.BankTransaction;

    let maxNew = getNewItemsMaxId(entity.Items) + 1;
    let items: IPaymentDocumentItemEntityExpanded[] = [];

    if (isBank) {
        await correctBpForLinkedDocument(storage, entity, Object.values(pairedDocs) as IPaymentDocumentItemEntity[]);
    }

    const splitData: IEntity[] = [];

    const transactionDate = getBankItemDefaultDateAccountingTransaction(storage);

    for (const [key, link] of Object.entries(pairedDocs)) {
        if (link.Amount === 0) {
            delete pairedDocs[key];
            continue;
        }

        const documentType = link?.LinkedDocument.DocumentTypeCode as DocumentTypeCode;
        const _isReceived = isReceived(documentType as DocumentTypeCode);

        const isTaxDocument = (link as IEntity)?.IsTaxDocument;

        const { pair, gains, splitRequestData } = (await createOrUpdatePair({
            documentType, storage, isReceived: _isReceived, isTaxDocument, isBank, transactionDate,
            maxId: maxNew++,
            transactionType: type,
            key: Number(key),
            pairedDoc: link
        }));

        if (splitRequestData) {
            splitData.push(splitRequestData);
        }

        items.push(pair);

        if (gains?.length > 0) {
            items = items.concat(gains);
            maxNew += gains.length;
        }
    }

    if (splitData.length > 0) {
        const url = `${CREATE_CLEARING_SPLIT_ITEMS}?CompanyId=${storage.context.getCompany().Id}`;
        const Payment = {
            AccountNumber: getFixAccountNumber(storage, isBank),
            PaymentDate: transactionDate,
            CurrencyCode: storage.context.getDefaultCurrency(),
            TransactionCurrencyCode: entity.TransactionCurrency?.Code,
            ExchangeRatePerUnit: entity.ExchangeRatePerUnit ?? 1,
            IsCredit: type === BankTransactionTypeCode.IncomingPayment,
            IsDebit: type === BankTransactionTypeCode.OutgoingPayment
        };

        const response = await fetch(url, {
            ...getDefaultPostParams(),
            body: JSON.stringify({
                Payment,
                ClearedDocuments: splitData
            })
        });

        const results = await response.json();
        for (const returnedItem of results) {
            const item = items.find(item => item.LinkedDocument.Id === returnedItem.DocumentId);
            let splitItems;

            if (returnedItem.SplitAccountAssignments?.length > 0) {
                let i = 0;
                splitItems = [];
                for (const split of returnedItem.SplitAccountAssignments) {
                    splitItems.push({
                        [BindingContext.NEW_ENTITY_ID_PROP]: `${BindingContext.NEW_ENTITY_ID_PROP} = ${i++}`,
                        Amount: split.Amount,
                        TransactionAmount: split.TransactionAmount,
                        DebitAccount: {
                            Name: split.DebitAccount.Name,
                            Number: split.DebitAccount.Number,
                            Id: split.DebitAccount.Id
                        },
                        CreditAccount: {
                            Name: split.CreditAccount.Name,
                            Number: split.CreditAccount.Number,
                            Id: split.CreditAccount.Id
                        },
                        Description: split.Description
                    });
                }
            }

            item.SplitAccountAssignments = splitItems;

            for (const ex of returnedItem.ExchangeRateDifferences) {
                items.push({
                    [BindingContext.NEW_ENTITY_ID_PROP]: ++maxNew,
                    Amount: ex.Amount,
                    TransactionAmount: ex.TransactionAmount,
                    DateAccountingTransaction: transactionDate,
                    LinkedDocument: { ...item.LinkedDocument },
                    PaymentDocumentItemTypeCode: ex.PaymentDocumentItemTypeCode,
                    AccountAssignmentSelection: {
                        AccountAssignment: {
                            Id: ex.AccountAssignmentSelection.AccountAssignment.Id,
                            Name: ex.AccountAssignmentSelection.AccountAssignment.Name,
                            ShortName: ex.AccountAssignmentSelection.AccountAssignment.ShortName,
                            CreditAccount: {
                                Id: ex.AccountAssignmentSelection.AccountAssignment.CreditAccount.Id,
                                Number: ex.AccountAssignmentSelection.AccountAssignment.CreditAccount.Number,
                                Name: ex.AccountAssignmentSelection.AccountAssignment.CreditAccount.Name
                            },
                            DebitAccount: {
                                Id: ex.AccountAssignmentSelection.AccountAssignment.DebitAccount.Id,
                                Number: ex.AccountAssignmentSelection.AccountAssignment.DebitAccount.Number,
                                Name: ex.AccountAssignmentSelection.AccountAssignment.DebitAccount.Name
                            }
                        },
                        Selection: {
                            Code: ex.AccountAssignmentSelection.Code
                        }
                    }
                });
            }
        }
    }

    const unpairedDocs = (storage.data.entity.Items || []).filter((link: IEntity) => {
        return !link.LinkedDocument?.Id;
    });

    const _setSplitError = (item: IPaymentDocumentItemEntity) => {
        const id = item.Id ?? `#id=${(item as TRecordAny)[BindingContext.NEW_ENTITY_ID_PROP]}`;
        const _path = `Items(${id})/AccountAssignmentSelection/AccountAssignment`;
        const bc = storage.data.bindingContext.navigate(_path);

        if (item.LinkedDocument?.Id && (
            !item.AccountAssignmentSelection?.Selection?.Code || (item.AccountAssignmentSelection?.Selection?.Code === SelectionCode.Split && !item.SplitAccountAssignments))) {
            storage.setError(bc, {
                message: i18next.t("Banks:Transactions.MissingSplit")
            });
        }
    };

    const mergedItems = items.concat(unpairedDocs).map((item) => {
        _setSplitError(item);
        return item;
    });

    storage.data.entity.Items = mergedItems;
    storage.handleFirstChange();
};

export const renderAssignmentSplitDialog = (storage: FormStorage<unknown, IAccAssDialogCustomData>): ReactElement => {
    const isOpened = storage.getCustomData().isSplitDialogOpened;

    if (!isOpened) {
        return null;
    }

    const dialogBc = storage.getCustomData().AccountAssignmentDialogBc;
    const bc = dialogBc.getParent().getParent().navigate("SplitAccountAssignments");

    return <AccountSplitDialog storage={storage} bindingContext={bc}/>;
};

export const bankAmountDueFormatter = (value: TValue, args: IGetValueArgs): ICellValueObject => {
    const formattedTransactionAmount = formatCurrency(Math.abs(Number(value ?? 0)), args.item.TransactionCurrencyCode);

    return {
        tooltip: formattedTransactionAmount,
        value: <b>{formattedTransactionAmount}</b>
    };
};

